<template>
  <div>
    <b-loading
      is-full-page
      :active="loading.fetchAuction || loading.general"
      :can-cancel="!loading.general"
    />
    <section class="hero is-primary is-bold">
      <div class="hero-body">
        <div class="container is-fluid">
          <div class="level is-flex-touch column-direction-touch auction-hero-level">
            <div class="level-left level-column margin-1">
              <div class="level-item">
                <div>
                  <h1 class="title">{{ auctionType }}</h1>
                  <br />
                  <div class="auction-info">
                    <ul>
                      <li v-if="auction.comprador.nome">
                        <strong class="has-text-white">Comprador:</strong>
                        {{
                          auction.comprador && auction.comprador.nome
                            ? capitalize(auction.comprador.nome)
                            : ''
                        }}
                      </li>

                      <li>
                        <strong class="has-text-white">Número:</strong>
                        {{ auction.numero_oc }}
                      </li>
                      <li>
                        <strong class="has-text-white">Unidade de compra:</strong>
                        {{ purchasingUnitLabel(auction.purchasingUnit) }}
                        <b-tag
                          v-if="purchasingUnitStatusLabel(auction.purchasingUnit)"
                          size="is-small"
                          :type="purchasingUnitStatusType(auction.purchasingUnit)"
                          class="ml-1"
                        >
                          {{ purchasingUnitStatusLabel(auction.purchasingUnit) }}
                        </b-tag>
                      </li>

                      <li>
                        <strong class="has-text-white">Tipo:</strong>
                        {{
                          auction.tipo_pregao === 'IMEDIATO'
                            ? 'Contratação Imediata'
                            : 'Registro de Preços'
                        }}
                      </li>

                      <li>
                        <strong class="has-text-white">Modo de Disputa:</strong>
                        {{ disputeModeLabel }}
                      </li>

                      <li>
                        <strong class="has-text-white">Inversão de Fases:</strong>
                        {{ auction.phase_inversion ? 'Sim' : 'Não' }}
                      </li>

                      <li>
                        <strong class="has-text-white">Negociação:</strong>
                        {{ auction.tipo_negociacao.nome }}
                      </li>

                      <li>
                        <strong class="has-text-white">Tipo de Edital:</strong>
                        {{ noticeType }}
                      </li>

                      <li v-if="reservedNotice">
                        <strong class="has-text-white">Preferência Local/Regional:</strong>
                        {{ auction.preferencia_regional ? 'Sim' : 'Não' }}
                      </li>

                      <li v-if="!auction.tipo_negociacao.id === 3">
                        <strong class="has-text-white">Divulgação de Referencial:</strong>
                        {{ auction.divulgar_referencial ? 'Sim' : 'Não' }}
                      </li>

                      <li>
                        <strong class="has-text-white">Origem dos Recursos:</strong>
                        {{ auction.origem_recursos.join(', ') }}
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            <div class="auction-hero-aside">
              <div class="auction-hero-meta">
                <div class="auction-hero-meta__item" v-if="isBidder && situation >= 5">
                  <div>
                    <p class="heading">Apelido</p>
                    <p class="title">{{ bidderAlias }}</p>

                    <p
                      v-if="situation === 6 && randomTime && isBidder"
                      class="title is-3 has-text-warning imminent-closure-text"
                    >
                      Encerramento Iminente
                    </p>
                  </div>
                </div>

                <div
                  v-if="verifySituation([6, 7, 11, 20]) && !randomTime"
                  class="auction-hero-meta__item"
                  :data-tour="situation === 20 ? 'manifestacao-intencao-relogio' : null"
                >
                  <App-Timer />
                </div>

                <div
                  class="auction-hero-meta__item"
                  v-if="situation === 6 && !isBidder && randomTime"
                >
                  <div>
                    <p class="title is-3 has-text-warning">Encerramento Iminente</p>
                  </div>
                </div>

                <div
                  class="auction-hero-meta__item"
                  v-if="heroDataLimite"
                  :data-tour="situation === 4 ? 'aguardando-inicio-previsao' : null"
                >
                  <div>
                    <p class="heading">{{ heroDataLimite.heading }}</p>
                    <p class="title">{{ heroDataLimite.date | formatDate }}</p>
                    <p class="auction-hero-meta__local">(Horário local)</p>
                  </div>
                </div>
              </div>

              <div class="auction-hero-status">
                <div class="auction-hero-meta__item">
                  <div data-tour="situacao">
                    <p class="heading">Situação</p>
                    <p class="title situacao-com-ajuda">
                      <span>{{ situacaoDisplayLabel }}</span>
                      <FeatureTourHelp
                        v-if="exibirAjudaTourSituacao"
                        :key="'ft-situacao-' + situation"
                        class="situacao-tour-help"
                        style="margin-left: 0.35rem"
                        :icon-only="true"
                        label=""
                        icon="help-circle-outline"
                        icon-size="is-small"
                        icon-font-size="1.15rem"
                        trigger-class="has-text-white"
                        contexto="auction.situation"
                        :feature-ctx="ctxTourSituacao"
                        @selecionar="reabrirTutorialSituacao"
                      />
                    </p>

                    <div class="auction-actions">
                      <button
                        v-if="situation === 10 && isCrier && !prazosRecursaisDefinidos"
                        data-tour="prazos-recursais-definir"
                        class="button is-primary is-inverted is-outlined level-button"
                        :class="{ 'is-loading': loadingPrazosRecursais }"
                        @click.prevent.stop="abrirDialogoPrazosRecursais(false)"
                      >
                        Definir Prazos
                      </button>

                      <button
                        v-if="
                          situation === 10 &&
                          isCrier &&
                          prazosRecursaisDefinidos &&
                          !todosRecursosEmStatusTerminal
                        "
                        data-tour="prazos-recursais-ajustar"
                        class="button is-primary is-inverted is-outlined level-button"
                        :class="{ 'is-loading': loadingPrazosRecursais }"
                        @click.prevent.stop="abrirDialogoPrazosRecursais(true)"
                      >
                        Ajustar Prazos
                      </button>

                      <button
                        v-if="displayDefinirPrazoManifestacaoIntencao"
                        data-tour="manifestacao-intencao-iniciar"
                        class="button is-primary is-inverted is-outlined level-button"
                        :class="{ 'is-loading': loadingPrazoManifestacaoIntencao }"
                        @click.prevent.stop="abrirDialogoPrazoManifestacaoIntencao"
                      >
                        Iniciar Manifestação
                      </button>

                      <button
                        v-if="displayAvancarInterposicaoRecursos"
                        data-tour="manifestacao-intencao-encerrar"
                        class="button is-primary is-inverted is-outlined level-button"
                        :class="{ 'is-loading': loadingPrazoManifestacaoIntencao }"
                        @click.prevent.stop="confirmarAvancarInterposicaoRecursos"
                      >
                        Encerrar Manifestação
                      </button>

                      <button
                        v-if="displayOpenSession"
                        data-tour="aguardando-inicio-abrir-sessao"
                        class="button is-primary is-inverted is-outlined level-button"
                        :class="{ ' is-loading': loading.startSession }"
                        @click.prevent.stop="startSession"
                      >
                        Abrir Sessão
                      </button>

                      <button
                        class="button is-primary is-inverted is-outlined level-button"
                        :disabled="disable.getMinutes"
                        v-if="renderGetMinutes"
                        data-tour="encerrada-recurso-obter-ata"
                        @click.prevent.stop="openConsiderationModal"
                      >
                        Obter Ata
                      </button>

                      <button
                        class="button is-primary is-inverted is-outlined level-button"
                        :disabled="disable.getMinutes"
                        v-if="renderGetAccountIntegration"
                        data-tour="encerrada-recurso-exportar"
                        @click.prevent.stop="getAccountIntegration"
                      >
                        Exportar Integração
                      </button>

                      <button
                        v-if="displayEndAnalysis"
                        class="button is-primary is-inverted is-outlined level-button"
                        :class="{ ' is-loading': loading.general }"
                        :disabled="analyzedProposals"
                        @click.prevent.stop="finishProposalsAnalysis()"
                      >
                        Encerrar Análise
                      </button>

                      <button
                        v-if="displayFinishBids && !itemsForBids"
                        class="button is-primary is-inverted is-outlined level-button"
                        :class="{ ' is-loading': loading.general }"
                        @click.prevent.stop="finishBids()"
                      >
                        Encerrar Lances
                      </button>

                      <button
                        v-if="displayFinishQualification"
                        class="button is-primary is-inverted is-outlined level-button"
                        :class="{ 'is-loading': loading.general }"
                        :disabled="loading.general"
                        @click.prevent.stop="finishQualification()"
                      >
                        Encerrar Habilitação
                      </button>

                      <button
                        v-if="displayFinishJudgment"
                        class="button is-primary is-inverted is-outlined level-button"
                        :class="{ 'is-loading': loading.general }"
                        :disabled="loading.general"
                        @click.prevent.stop="finishJudgment()"
                      >
                        Encerrar Julgamento
                      </button>

                      <button
                        v-if="
                          (happenedApproval || !hasAnUnfinishedItem) && situation === 10 && isCrier
                        "
                        :disabled="disableFinishSession"
                        class="button is-primary is-inverted is-outlined level-button"
                        :class="{ ' is-loading': loading.finishSession }"
                        @click.prevent.stop="finishSession(auction.id)"
                      >
                        Encerrar Sessão
                      </button>

                      <button
                        v-if="
                          !happenedApproval && hasAnUnfinishedItem && situation === 10 && isCrier
                        "
                        :disabled="disableFinishSession && !happenedApproval"
                        class="button is-primary is-inverted is-outlined level-button"
                        :class="{ ' is-loading': loading.general }"
                        @click.prevent.stop="startBiddingStep(auction.id)"
                      >
                        {{ itemsForBids ? 'Iniciar Lances' : 'Iniciar Negociação' }}
                      </button>

                      <div
                        v-if="displaySuspend && displaySuspendSineDie"
                        class="level-button auction-actions__suspend-split"
                      >
                        <button
                          class="button is-primary is-inverted is-outlined auction-actions__suspend-main"
                          :class="{ ' is-loading': loading.suspendSession }"
                          :disabled="loading.suspendSession"
                          @click.prevent.stop="openModal(auction.id, 'suspend')"
                        >
                          Suspender
                        </button>
                        <b-dropdown
                          position="is-bottom-left"
                          class="auction-actions__suspend-menu"
                          aria-role="list"
                        >
                          <button
                            slot="trigger"
                            class="button is-primary is-inverted is-outlined auction-actions__suspend-toggle"
                            type="button"
                            :disabled="loading.suspendSession"
                            aria-label="Mais opções de suspensão"
                          >
                            <b-icon icon="menu-down" size="is-small" />
                          </button>
                          <b-dropdown-item
                            aria-role="listitem"
                            @click="openModal(auction.id, 'suspendSineDie')"
                          >
                            Suspender Sine Die
                          </b-dropdown-item>
                        </b-dropdown>
                      </div>

                      <button
                        v-else-if="displaySuspend"
                        class="action-button button is-primary is-inverted is-outlined level-button"
                        :class="{ ' is-loading': loading.suspendSession }"
                        @click.prevent.stop="openModal(auction.id, 'suspend')"
                      >
                        Suspender
                      </button>

                      <button
                        v-if="displaySuspendSineDieEDP"
                        class="action-button button is-primary is-inverted is-outlined level-button"
                        :class="{ ' is-loading': loading.suspendSession }"
                        @click.prevent.stop="openModal(auction.id, 'suspendSineDie')"
                      >
                        Suspender Sine Die
                      </button>

                      <b-dropdown
                        v-if="displaySecondaryActionsMenu"
                        position="is-bottom-left"
                        class="level-button auction-actions__more"
                        aria-role="list"
                      >
                        <button
                          slot="trigger"
                          class="button is-primary is-inverted is-outlined"
                          type="button"
                          aria-label="Mais ações"
                          :data-tour="
                            opcaoEncerrarPrazoRecursal ? 'prazos-recursais-encerrar' : undefined
                          "
                        >
                          <b-icon icon="dots-vertical" size="is-small" />
                        </button>

                        <b-dropdown-item
                          v-if="loaded && activeAuction && provider"
                          aria-role="listitem"
                          @click="watchElectronicAuction()"
                        >
                          {{ auctionWatch.id ? 'Não Assistir' : 'Assistir' }}
                        </b-dropdown-item>

                        <b-dropdown-item
                          v-if="renderGetMinutes"
                          :disabled="disable.getMinutes"
                          aria-role="listitem"
                          @click="openConsiderationModal"
                        >
                          Obter Ata
                        </b-dropdown-item>

                        <b-dropdown-item
                          v-if="renderGetAccountIntegration"
                          :disabled="disable.getMinutes"
                          aria-role="listitem"
                          @click="getAccountIntegration"
                        >
                          Exportar Integração
                        </b-dropdown-item>

                        <b-dropdown-item
                          v-if="!isCrier && (proposals.length || proposalsDeleted.length) && logged"
                          :disabled="loading.generateExtract"
                          aria-role="listitem"
                          @click="generateProposalsExtractHandler"
                        >
                          Baixar Extrato Propostas
                        </b-dropdown-item>

                        <b-dropdown-item
                          v-if="isCrier && logged"
                          :disabled="loading.generateExtract"
                          aria-role="listitem"
                          @click="goToAttachments"
                        >
                          Editar {{ auctionTypeName }}
                        </b-dropdown-item>

                        <b-dropdown-item
                          v-if="displayBackToProposals"
                          :disabled="loading.general"
                          aria-role="listitem"
                          data-tour="voltar-etapa"
                          @click="goBackToPreviousStage()"
                        >
                          Voltar Etapa
                        </b-dropdown-item>

                        <b-dropdown-item
                          v-if="opcaoEncerrarPrazoRecursal"
                          :disabled="loadingPrazosRecursais"
                          aria-role="listitem"
                          @click="confirmarEncerrarPrazoRecursal"
                        >
                          {{ opcaoEncerrarPrazoRecursal.label }}
                        </b-dropdown-item>

                        <b-dropdown-item
                          v-if="
                            logged &&
                            allowedToChangeCrier &&
                            ((situation >= 5 && situation < 11) ||
                              situation === 17 ||
                              situation === 19)
                          "
                          :disabled="loading.alterarPregoeiro"
                          aria-role="listitem"
                          @click="openAlterarPregoeiroModal"
                        >
                          {{ textButtonChangeCrier }}
                        </b-dropdown-item>

                        <b-dropdown-item
                          v-if="displayDefineRetorno"
                          :disabled="loading.general"
                          aria-role="listitem"
                          @click="openModal(auction.id, 'insertReopeningDate')"
                        >
                          Definir data de retorno
                        </b-dropdown-item>

                        <b-dropdown-item
                          v-if="displayChangeToSineDie"
                          :disabled="loading.suspendSession"
                          aria-role="listitem"
                          @click="openModal(auction.id, 'changeToSineDie')"
                        >
                          Alterar para Suspensão Sine Die
                        </b-dropdown-item>

                        <b-dropdown-item
                          v-if="displayReopenSession"
                          :disabled="loading.general"
                          aria-role="listitem"
                          @click="reopenSession(auction.id)"
                        >
                          Reabrir Sessão
                        </b-dropdown-item>

                        <b-dropdown-item
                          v-if="displayCancelOrRevokeAuction"
                          :disabled="loading.revoke"
                          aria-role="listitem"
                          @click="openModal(auction.id, 'revoke')"
                        >
                          Revogar
                        </b-dropdown-item>

                        <b-dropdown-item
                          v-if="displayCancelOrRevokeAuction"
                          :disabled="loading.cancel"
                          aria-role="listitem"
                          @click="openModal(auction.id, 'cancel')"
                        >
                          Anular
                        </b-dropdown-item>
                      </b-dropdown>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div v-if="displayPendingWarning" class="columns">
            <div class="column has-text-centered">
              <b-icon
                class="icon-pending-company"
                type="is-warning"
                icon="alert-box"
                size="is-large"
              />
              <span class="text-pending-company">
                Empresa pendente de aprovação da SelCorp. Caso não tenha solicitado validação, por
                favor solicite no perfil de sua empresa.
              </span>
            </div>
          </div>
        </div>
      </div>
    </section>

    <div v-if="needReload && loaded && !logging" class="disconnection-content">
      <b-notification
        indefinite
        class="disconnection-notification"
        role="alert"
        type="is-warning"
        :closable="false"
      >
        <b-icon class="notification-icon" icon="alert-box" size="is-medium" />
        <span class="notification-text"
          >O navegador perdeu a conexão com o servidor. Por favor, recarregue a página ou clique
          <a class="notification-link" @click.stop.prevent="reloadPage()">aqui</a> para
          recarregar.</span
        >
      </b-notification>
    </div>

    <div class="limit">
      <div class="auction-container">
        <section>
          <b-tabs
            expanded
            destroy-on-hide
            ref="tabs"
            type="is-toggle"
            :animated="false"
            v-model="selectedTab"
          >
            <b-tab-item label="Comprador">
              <Auction-Buyer class="components" />
            </b-tab-item>

            <b-tab-item label="Edital/Anexos">
              <Auction-Attachments class="components" />
            </b-tab-item>

            <b-tab-item>
              <template #header>
                <span :data-tour="situation === 4 ? 'aguardando-inicio-itens' : null">Itens</span>
              </template>
              <Send-Documents
                class="components"
                v-if="display.sendDocuments"
                :items="items"
                :item="item"
                :qualificationCompany="qualificationCompany"
              />
              <Auction-Offers
                class="components"
                v-if="display.offers"
                :item="item"
                :ofertaCompraId="auctionId"
              />
              <Auction-Items class="components" ref="items" v-if="display.items" :loaded="loaded" />
              <BatchProposalSender
                v-if="display.batchItems"
                class="components"
                :item="item"
                :proposals="proposals"
                @voltar="closeBatchSender"
              />
              <ReadjustProposals
                v-if="display.readjustProposals.open"
                class="components"
                :item="item"
                :proposal="display.readjustProposals.proposal"
                :isCrier="isCrier"
                :oferta-compra-id="auctionId"
                @voltar="closeReadjustProposals"
              />
              <JudgmentDiligenceUpload
                v-if="display.judgmentDiligenceUpload"
                class="components"
                :item="item"
                :diligence="judgmentDiligence"
                :company="qualificationCompany"
              />
              <JudgmentDiligenceReview
                v-if="display.judgmentDiligenceReview"
                class="components"
                :item="item"
                :diligence="judgmentDiligence"
                :company="qualificationCompany"
              />
            </b-tab-item>

            <b-tab-item :disabled="!loaded" v-if="displayDocumentsTab">
              <template slot="header"
                ><b-icon
                  v-if="proposalsDelivery"
                  class="custom-icon"
                  :icon="requiredDocumentsSent ? 'check' : 'close'"
                  :type="requiredDocumentsSent ? 'is-success' : 'is-danger'"
                ></b-icon
                >{{ proposalsDelivery ? 'Documentos' : 'Documentos Aceitos' }}</template
              >

              <Send-Documents class="components" v-if="proposalsDelivery" />
              <Documents-List v-else class="components" />
            </b-tab-item>

            <b-tab-item label="Esclarecimentos/Impugnações">
              <Clarifications-Impugnations-Wrapper
                class="components"
                :internal-tab-selected="internalTabSelected"
              />
            </b-tab-item>

            <b-tab-item label="Gestão de Prazos">
              <Auction-Deadlines class="components" />
            </b-tab-item>

            <b-tab-item v-if="canAccessArpTab">
              <template #header>
                <span data-tour="sessao-arp">ARP</span>
              </template>
              <ArpHub class="components" />
            </b-tab-item>

            <b-tab-item v-if="verifySituation([12, 13, 14, 15, 16]) && auction.hasAta">
              <template #header>
                <span data-tour="encerrada-recurso-ata">Ata</span>
              </template>
              <object height="1000px" type="application/pdf" width="100%" :data="linkPdf">
                <div class="minutes-pdf-download">
                  <p class="minutes-pdf-download-text">O navegador não suporta PDF.</p>

                  <a
                    download
                    class="button is-primary is-medium"
                    :href="fileServer(getMinutesPdfName)"
                    >Download Ata</a
                  >
                </div>
              </object>
            </b-tab-item>

            <b-tab-item
              v-if="
                verifySituation([15, 16]) &&
                (auction.hasTermoAdjudicacao || auction.hasTermoHomologacao)
              "
            >
              <template #header>
                <span data-tour="encerrada-recurso-termos">Termos</span>
              </template>
              <AuctionTermsWrapper
                class="components"
                :internal-tab-selected="internalTabSelected"
              />
            </b-tab-item>

            <b-tab-item v-if="displayAppealsTab">
              <template #header>
                <span data-tour="prazos-recursais-aba-recursos">Recursos</span>
              </template>
              <Auction-Appeals class="components" />
            </b-tab-item>

            <b-tab-item v-if="situation > 11 && situation < 17">
              <template #header>
                <span data-tour="encerrada-recurso-atos">Atos Decisórios</span>
              </template>
              <Decision-Acts class="components" />
            </b-tab-item>
          </b-tabs>
        </section>
      </div>
    </div>

    <b-modal
      has-modal-card
      ref="justificationModal"
      :active.sync="justification.active"
      :onCancel="resetLoadings"
      @justificate="sendJustification"
    >
      <AppJustification
        v-if="justification.active"
        :displayDateTime="justification.displayDateTime"
        :displayFailButton="justification.displayFail"
        :displaySuccessButton="justification.displaySuccess"
        :failText="justification.failText"
        :successText="justification.successText"
        :suspensionText="justification.displaySuspendText"
        :workDays="justification.displayWorkingDays"
        :skipDoubleConfirm="justification.skipDoubleConfirm"
        :titleText="justification.titleText"
        :placeholderText="justification.placeholderText"
        :introText="justification.introText"
      />
    </b-modal>

    <b-modal
      has-modal-card
      ref="sendTimeModal"
      :active.sync="reopeningDate.active"
      :onCancel="resetLoadings"
      @send-date-time="processReopeningDate"
    >
      <AppSendDateTime
        v-if="reopeningDate.active"
        :automaticSuspensionDate="reopeningDate.automaticSuspensionDate"
        :loading="reopeningDate.loading"
        :maximumDate="reopeningDate.maximumDate"
        :minimumDate="reopeningDate.minimumDate"
      />
    </b-modal>

    <b-modal
      has-modal-card
      ref="considerationModal"
      :active.sync="consideration"
      :onCancel="resetLoadings"
      @considerar="generateMinutes"
    >
      <Considerations v-if="consideration" />
    </b-modal>

    <b-modal
      has-modal-card
      :active.sync="backStageSelection.active"
      :onCancel="onBackStageSelectionCancel"
    >
      <div v-if="backStageSelection.active">
        <section class="hero is-fullheight">
          <div class="hero-body">
            <div class="container has-text-centered">
              <h3 class="title has-text-white">Voltar etapa</h3>
              <div class="box app-justification-box">
                <div class="back-stage-selection__message">
                  Escolha para qual etapa deseja voltar.
                </div>

                <b-field class="back-stage-selection__options">
                  <b-select
                    expanded
                    placeholder="Selecione a etapa"
                    v-model="backStageSelection.selectedDestino"
                  >
                    <option
                      v-for="option in backStageSelection.options"
                      :key="option.destino"
                      :value="option.destino"
                    >
                      {{ getStageLabel(option.destino) }}
                    </option>
                  </b-select>
                </b-field>

                <div class="buttons-container has-text-right">
                  <button
                    class="button back-stage-selection__cancel-button"
                    @click.prevent="onBackStageSelectionCancel"
                  >
                    Cancelar
                  </button>
                  <button
                    class="button is-primary"
                    :disabled="!backStageSelection.selectedDestino"
                    @click.prevent="confirmBackStageSelection"
                  >
                    Continuar
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </b-modal>

    <b-modal has-modal-card :active.sync="alterarPregoeiroModal" :onCancel="resetLoadings">
      <div v-if="alterarPregoeiroModal">
        <section class="hero is-fullheight">
          <div class="hero-body">
            <div class="container has-text-centered">
              <h3 class="title has-text-white">Alterar Pregoeiro</h3>
              <div class="box app-justification-box">
                <b-loading is-full-page :active="loading.alterarPregoeiro" can-cancel />

                <template v-if="!loading.alterarPregoeiro">
                  <form>
                    <b-field label="Novo Pregoeiro">
                      <b-autocomplete
                        class="auction-responsible-autocomplete"
                        ref="responsavelAlterarPregoeiro"
                        v-model="responsavelAlterarPregoeiro"
                        placeholder="Insira um e-mail"
                        field="userEmail"
                        :data="pesquisaAlterarPregoeiro"
                        :loading="pesquisandoAlterarPregoeiro"
                        @typing="
                          selectedNovoPregoeiroUser = null;
                          pesquisarAlterarPregoeiro();
                        "
                        @select="onSelectNovoPregoeiroUser"
                      >
                        <template v-slot:empty
                          >Nenhum resultado para {{ responsavelAlterarPregoeiro }}</template
                        >

                        <template v-slot:header>
                          <div class="media header-complete-alterar-pregoeiro">
                            <div class="media-left email-media-alterar-pregoeiro">
                              <b>E-mail</b>
                            </div>
                            <div class="media-content name-media-alterar-pregoeiro">
                              <b>Nome</b>
                            </div>
                          </div>
                        </template>

                        <template v-slot="props">
                          <div class="media">
                            <div class="media-left email-media-alterar-pregoeiro">
                              {{ props.option.userEmail }}
                            </div>
                            <div class="media-content name-media-alterar-pregoeiro">
                              {{ props.option.name }}
                            </div>
                          </div>
                        </template>
                      </b-autocomplete>
                    </b-field>
                  </form>

                  <hr />

                  <b-table hoverable v-if="responsaveis.length" :data="responsaveis">
                    <b-table-column label="E-mail" v-slot="props">{{
                      props.row.email
                    }}</b-table-column>

                    <b-table-column label="Responsável" v-slot="props">{{
                      props.row.nome
                    }}</b-table-column>

                    <b-table-column label="Função" v-slot="props">
                      <div class="column-funcao-alterar-pregoeiro">
                        {{ funcaoNomeAlterarPregoeiro(props.row.funcao) }}
                      </div>
                    </b-table-column>
                  </b-table>

                  <div v-else class="panel">
                    <div class="panel-block is-justify-content-center">
                      <p class="has-text-grey">Nenhum responsável cadastrado neste pregão.</p>
                    </div>
                  </div>
                </template>

                <div class="buttons-container mt-4">
                  <button
                    class="button is-outlined"
                    @click.stop.prevent="alterarPregoeiroModal = false"
                  >
                    Fechar
                  </button>

                  <button
                    class="button is-primary ml-3"
                    :class="{ ' is-loading': loading.confirmarAlterarPregoeiro }"
                    :disabled="!selectedNovoPregoeiroUser || loading.confirmarAlterarPregoeiro"
                    @click.stop.prevent="confirmarAlterarPregoeiro"
                  >
                    Confirmar
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </b-modal>

    <AuctionChat v-if="situation >= 4" :tour-anchor="chatTourAnchor" />

    <b-modal
      has-modal-card
      :active.sync="dialogoPrazosRecursais"
      :onCancel="resetLoadings"
      @confirmar-prazos-recursais="confirmarPrazosRecursais"
    >
      <PrazosRecursaisDialog
        v-if="dialogoPrazosRecursais"
        :titulo="dialogoPrazosRecursaisAjuste ? 'Ajustar Prazos' : 'Definir Prazos'"
        :ajuste="dialogoPrazosRecursaisAjuste"
        :regras-ajuste="regrasAjustePrazosRecursais"
        :loading="loadingPrazosRecursais"
        :sugestoes="sugestoesPrazosRecursais"
        :linha-do-tempo="linhaDoTempoPrazosRecursais"
      />
    </b-modal>

    <b-modal
      has-modal-card
      :active.sync="dialogoPrazoManifestacaoIntencao"
      :onCancel="resetLoadings"
      @confirmar-prazo-manifestacao-intencao="confirmarPrazoManifestacaoIntencao"
    >
      <PrazoManifestacaoIntencaoDialog
        v-if="dialogoPrazoManifestacaoIntencao"
        titulo="Iniciar Manifestação"
        :ajuste="false"
        :limites="limitesPrazoManifestacaoIntencao"
        :loading="loadingPrazoManifestacaoIntencao"
      />
    </b-modal>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex';
import debounce from 'lodash/debounce';
import Holidays from 'date-holidays';

import { search } from '@/resources/companies/companiesUsers';
import { changeCrier } from '@/resources/ofertasCompra';

import AppJustification from '@/components/AppJustification';
import PrazosRecursaisDialog from '@/components/PrazosRecursaisDialog';
import PrazoManifestacaoIntencaoDialog from '@/components/PrazoManifestacaoIntencaoDialog';
import AppSendDateTime from '@/components/AppSendDateTime';
import BatchProposalSender from '@/components/BatchProposalSender';
import ReadjustProposals from '@/components/ReadjustProposals';
import AppTimer from '@/components/AppTimer';
import AuctionAppeals from '@/views/Pregao/AuctionContainer/Recursos';
import AuctionAttachments from '@/views/Pregao/AuctionContainer/Edital';
import AuctionBuyer from '@/views/Pregao/AuctionContainer/Comprador';
import AuctionChat from '@/views/Pregao/AuctionContainer/AuctionChat';
import AuctionDeadlines from '@/views/Pregao/AuctionContainer/Prazos';
import AuctionItems from '@/views/Pregao/AuctionContainer/Items/ItemsWrapper';
import AuctionOffers from '@/views/Pregao/AuctionContainer/Offers';
import ClarificationsImpugnationsWrapper from '@/views/Pregao/AuctionContainer/ClarificationsImpugnations/ClarificationsImpugnationsWrapper';
import AuctionTermsWrapper from '@/views/Pregao/AuctionContainer/Terms/AuctionTermsWrapper';
import Considerations from '@/views/Pregao/AuctionContainer/Consideracao';
import DecisionActs from '@/views/Pregao/AuctionContainer/DecisionActs';
import DocumentsList from '@/views/Pregao/AuctionContainer/Documents/DocumentsList';
import SendDocuments from '@/views/Pregao/AuctionContainer/Documents/SendDocuments';
import JudgmentDiligenceUpload from '@/views/Pregao/AuctionContainer/JudgmentDiligence/JudgmentDiligenceUpload';
import JudgmentDiligenceReview from '@/views/Pregao/AuctionContainer/JudgmentDiligence/JudgmentDiligenceReview';
import ArpHub from '@/views/Pregao/Arp/ArpHub';

import SocketResource from '@/resources/socket';

import { capitalize, fileServer } from '@/utils/functions';
import { formatarData as formatDate } from '@/utils/format';
import { calcularPrazosRecursaisPadrao } from '@/utils/businessDays';
import { isRecursoStatusTerminal } from '@/utils/recursosConstants';
import { funcoes, SituacaoEnum, SituacaoLabel, getOfertaCompraEnum } from '@/utils/enumerados';
import sanitizeFileName from '@/utils/sanitizes';
import featureTourMixin from '@/mixins/featureTour';
import FeatureTourHelp from '@/components/FeatureTourHelp';
import { isTourActive } from '@/feature-tours';

export default {
  name: 'AuctionContainer',
  mixins: [featureTourMixin],
  components: {
    AppJustification,
    FeatureTourHelp,
    PrazosRecursaisDialog,
    PrazoManifestacaoIntencaoDialog,
    AppSendDateTime,
    AppTimer,
    AuctionAppeals,
    AuctionAttachments,
    AuctionBuyer,
    AuctionChat,
    AuctionDeadlines,
    AuctionItems,
    AuctionOffers,
    ClarificationsImpugnationsWrapper,
    AuctionTermsWrapper,
    BatchProposalSender,
    ReadjustProposals,
    Considerations,
    DecisionActs,
    DocumentsList,
    SendDocuments,
    JudgmentDiligenceUpload,
    JudgmentDiligenceReview,
    ArpHub,
  },
  data() {
    const disable = {
      getMinutes: false,
    };
    const display = {
      offers: false,
      items: true,
      batchItems: false,
      readjustProposals: { open: false, proposal: null },
      sendDocuments: false,
      judgmentDiligenceUpload: false,
      judgmentDiligenceReview: false,
    };
    const loading = {
      alterarPregoeiro: false,
      confirmarAlterarPregoeiro: false,
      cancel: false,
      fetchAuction: false,
      finishSession: false,
      general: false,
      revoke: false,
      startSession: false,
      suspendSession: false,
      watchAuction: false,
      generateExtract: false,
    };
    const justification = {
      active: false,
      displayDateTime: false,
      displayFail: false,
      displaySuccess: false,
      displaySuspendText: false,
      displayBackToProposals: false,
      displayWorkingDays: false,
      failText: '',
      successText: 'Classificar',
      pendingPayload: null,
      skipDoubleConfirm: false,
      titleText: 'Justificativa',
      placeholderText: '',
      introText: '',
    };
    const reopeningDate = {
      active: false,
      automaticSuspensionDate: null,
      loading: false,
      maximumDate: null,
      minimumDate: null,
    };
    const auctionType = '';

    const funcoesAlterarPregoeiro = [
      { id: 1, nome: 'Autoridade Competente', enum: 'AUTORIDADE' },
      { id: 2, nome: 'Equipe de Apoio', enum: 'APOIO' },
      { id: 3, nome: 'Pregoeiro(a)', enum: 'PREGOEIRO' },
      { id: 4, nome: 'Pregoeiro(a) Substituto(a)', enum: 'PREGOEIRO-SUBSTITUTO' },
      { id: 5, nome: 'Subscritor(a) de Edital', enum: 'ESCRIVAO' },
      { id: 6, nome: 'Agente de Contratação', enum: 'AGENTE-CONTRATACAO' },
      { id: 7, nome: 'Agente de Contratação Substituto(a)', enum: 'AGENTE-CONTRATACAO-SUBSTITUTO' },
    ];

    return {
      action: '',
      alterarPregoeiroModal: false,
      funcoesAlterarPregoeiro,
      pesquisaAlterarPregoeiro: [],
      pesquisandoAlterarPregoeiro: false,
      responsavelAlterarPregoeiro: '',
      selectedNovoPregoeiroUser: null,
      auctionType,
      auctionId: 0,
      capitalize,
      consideration: false,
      disable,
      display,
      fileServer,
      holidays: new Holidays('BR'),
      internalTabSelected: 0,
      item: null,
      justification,
      loaded: false,
      loading,
      needReload: false,
      qualificationCompany: null,
      judgmentDiligence: null,
      reopeningDate,
      selectedTab: 2,
      socketResource: new SocketResource(this.$socket),
      dialogoPrazosRecursais: false,
      dialogoPrazosRecursaisAjuste: false,
      loadingPrazosRecursais: false,
      dialogoPrazoManifestacaoIntencao: false,
      loadingPrazoManifestacaoIntencao: false,
      backStageSelection: {
        active: false,
        options: [],
        selectedDestino: '',
      },
      backStageSelectionResolver: null,
      backStageOptions: [],
      backStageOptionsRequestId: 0,
    };
  },
  beforeRouteLeave(to, from, next) {
    this.socketResource.leaveAuction(this.auction.id);
    document.title = 'SelCorp - Sistema Eletrônico de Licitações';
    this.limparPregao();
    this.limparAnexosPregao();
    this.limparItensPregao();
    this.limparResponsaveisPregao();
    this.cleanAuctionProposals();
    this.limparLancesPregao();
    this.cleanAuctionDeclarations();
    this.cleanAuctionDocuments();
    this.invalidatePin();
    return next();
  },
  computed: {
    ...mapState('app', ['windowWidth']),
    ...mapState('auctionsClarifications', ['clarifications']),
    ...mapState('proposals', { allProposals: 'proposals', proposalsDeleted: 'proposalsDeleted' }),
    ...mapState('auctionsDocuments', ['auctionDocuments']),
    ...mapState('auctionsImpugnations', ['impugnations']),
    ...mapState('companyBilling', ['status', 'currentBillingMethodId', 'companyCredits']),
    ...mapState('auctionsOptions', ['auctionWatch']),
    ...mapState('companies', ['company']),
    ...mapGetters('proposals', { proposals: 'obterPropostasUsuarioLogado' }),
    ...mapState('auctionsBidders', ['userLoggedBidder']),
    ...mapState('requiredDocuments', ['requiredDocuments']),
    ...mapGetters({
      appeals: 'obterOfertaCompraRecursos',
      auction: 'obterOfertaCompra',
      hasAppealIntention: 'verificarOfertaCompraIntencaoRecursoManifestada',
      bids: 'obterOfertaCompraLances',
      deadlines: 'obterOfertaCompraPrazos',
      logged: 'getLogged',
      logging: 'getLogging',
      items: 'obterTodosItens',
      responsaveis: 'obterOfertaCompraResponsaveis',
      user: 'getUser',
      userFunction: 'getFunction',
      prazosRecursaisDefinidos: 'prazosRecursaisDefinidos',
      prazosRecursaisHero: 'prazosRecursaisHero',
      obterPrazosRecursais: 'obterPrazosRecursais',
      janelaRecursalVigente: 'janelaRecursalVigente',
      prazoManifestacaoIntencao: 'obterPrazoManifestacaoIntencao',
      prazoManifestacaoIntencaoDefinido: 'prazoManifestacaoIntencaoDefinido',
      prazoManifestacaoIntencaoVigente: 'prazoManifestacaoIntencaoVigente',
      canAccessArpTab: 'canAccessArpTab',
      arpJourney: 'obterArpJourney',
    }),
    heroDataLimite() {
      if (this.verifySituation([1, 2, 3, 4])) {
        return {
          heading: 'Previsão de Abertura',
          date: this.auction.data_abertura,
        };
      }

      if (this.situation === 10 && this.prazosRecursaisHero) {
        return this.prazosRecursaisHero;
      }

      // Sit. 20: o prazo fica no AppTimer (igual aos lances), sem quadro de data limite.

      if (this.auction.data_reabertura) {
        return {
          heading: 'Previsão de Retorno',
          date: this.auction.data_reabertura,
        };
      }

      return null;
    },
    sugestoesPrazosRecursais() {
      if (this.dialogoPrazosRecursaisAjuste && this.prazosRecursaisDefinidos) {
        const prazos = this.obterPrazosRecursais;
        return {
          razoes: prazos.dataLimiteRazoes,
          contrarrazoes: prazos.dataLimiteContrarrazoes,
          julgamento: prazos.dataLimiteJulgamento,
        };
      }

      const padrao = calcularPrazosRecursaisPadrao(new Date());
      return {
        razoes: padrao.razoes,
        contrarrazoes: padrao.contrarrazoes,
        julgamento: padrao.julgamento,
      };
    },
    regrasAjustePrazosRecursais() {
      return this.obterPrazosRecursais && this.obterPrazosRecursais.regrasAjuste
        ? this.obterPrazosRecursais.regrasAjuste
        : null;
    },
    linhaDoTempoPrazosRecursais() {
      return this.obterPrazosRecursais?.linhaDoTempo || null;
    },
    displayDefinirPrazoManifestacaoIntencao() {
      return (
        this.isCrier &&
        this.situation === SituacaoEnum.MANIFESTACAO_INTENCAO_RECURSO &&
        !this.prazoManifestacaoIntencaoVigente
      );
    },
    displayAvancarInterposicaoRecursos() {
      // Só após o relógio ter sido iniciado ao menos uma vez e já ter parado
      // (API rejeita encerrar com relógio vigente; na 1ª entrada só "Iniciar").
      return (
        this.isCrier &&
        this.situation === SituacaoEnum.MANIFESTACAO_INTENCAO_RECURSO &&
        this.prazoManifestacaoIntencaoDefinido &&
        !this.prazoManifestacaoIntencaoVigente
      );
    },
    limitesPrazoManifestacaoIntencao() {
      return (
        this.prazoManifestacaoIntencao?.limites || {
          minMinutos: 10,
          maxMinutos: 24 * 60,
        }
      );
    },
    auctionTypeName() {
      return getOfertaCompraEnum(this.auction.tipo).titleSingular;
    },
    textButtonChangeCrier() {
      if (['CONCORRENCIA', 'DISPENSA'].includes(this.auction.tipo)) {
        return 'Alterar Agente de Contratação';
      }

      return 'Alterar Pregoeiro';
    },
    allowedToChangeCrier() {
      return this.isCrier || this.isAuthority || this.isSubstitute;
    },
    activeAuction() {
      return (
        (this.situation > SituacaoEnum.CADASTRADA && this.situation < SituacaoEnum.FRACASSADA) ||
        this.situation === SituacaoEnum.SUSPENSA
      );
    },
    adhesionStep() {
      // True only during Adesão de Preço or Habilitação da Adesão — not merely
      // because the auction once had a prazo 13.
      if (this.situation === SituacaoEnum.ADESAO_DE_PRECO) {
        return true;
      }
      if (this.situation === SituacaoEnum.HABILITACAO) {
        return this.isAdhesionQualificationContext;
      }
      return false;
    },
    currentQualificationDeadline() {
      return this.deadlines.find(prazo => Number(prazo.prazo_id) === 11 && !prazo.data_ate);
    },
    latestClosedAdhesionDeadline() {
      const closedAdhesionDeadlines = this.deadlines
        .filter(prazo => Number(prazo.prazo_id) === 13 && prazo.data_ate)
        .sort((a, b) => new Date(b.data_ate).getTime() - new Date(a.data_ate).getTime());

      return closedAdhesionDeadlines[0] || null;
    },
    isAdhesionQualificationContext() {
      const deadline = this.currentQualificationDeadline;
      const contextFromField = (
        deadline?.qualification_context ||
        deadline?.qualificationContext ||
        ''
      )
        .toString()
        .toUpperCase();
      if (contextFromField === 'ADESAO') return true;
      if (contextFromField === 'NORMAL') return false;

      const context = deadline?.observacao;
      if (typeof context === 'string') {
        const normalizedContext = context.toUpperCase();
        if (normalizedContext.includes('QUALIFICATION_CONTEXT:ADESAO')) {
          return true;
        }
        if (normalizedContext.includes('QUALIFICATION_CONTEXT:NORMAL')) {
          return false;
        }
      }

      // Fallback: prazo de habilitação aberto cujo nome já veio rotulado como adesão.
      if (
        typeof deadline?.nome === 'string' &&
        /habilita[cç][aã]o da ades[aã]o/i.test(deadline.nome)
      ) {
        return true;
      }

      const qualificationStart = new Date(deadline?.data_de).getTime();
      const adhesionEnd = new Date(this.latestClosedAdhesionDeadline?.data_ate).getTime();
      const ONE_SECOND_MS = 1000;

      if (!Number.isFinite(qualificationStart) || !Number.isFinite(adhesionEnd)) {
        return false;
      }

      return Math.abs(qualificationStart - adhesionEnd) <= ONE_SECOND_MS;
    },
    analyzedProposals() {
      const filtro = this.allProposals.filter(
        proposta => ['DESERTO', 'FRACASSADA', 'REVOGADO'].indexOf(proposta.item_estado) === -1,
      );
      return !filtro.every(
        proposta => proposta.estado === 'CLASSIFICADA' || proposta.estado === 'DESCLASSIFICADA',
      );
    },
    isAuthority() {
      return this.userFunction === funcoes.AUTORIDADE;
    },
    isBidder() {
      return this.userFunction === funcoes.LICITANTE;
    },
    bidderAlias() {
      return this.userLoggedBidder.alias;
    },
    isBuyer() {
      return this.company.buyer && this.company.id === this.auction.comprador.id;
    },
    closedMode() {
      return this.items.some(
        item => ['LANCE-FECHADO', 'PRIMEIRA-CHAMADA', 'NOVA-CHAMADA'].indexOf(item.estado) > -1,
      );
    },
    closedDisputeMode() {
      return this.auction.modo_disputa === 'FECHADO';
    },
    disputeModeLabel() {
      const disputeModeMap = {
        ABERTO: 'Aberto',
        FECHADO: 'Aberto/Fechado',
        FECHADO_ABERTO: 'Fechado/Aberto',
        COM_DISPUTA: 'Com Disputa',
        SEM_DISPUTA: 'Sem Disputa',
      };

      return disputeModeMap[this.auction?.modo_disputa] || '';
    },
    isCrier() {
      return this.userFunction === funcoes.PREGOEIRO;
    },
    isSubstitute() {
      return (
        this.userFunction === funcoes.PREGOEIRO_SUBSTITUTO ||
        this.userFunction === funcoes.AGENTE_CONTRATACAO_SUBSTITUTO
      );
    },
    isAgenteContratacao() {
      return (
        this.userFunction === funcoes['AGENTE-CONTRATACAO'] ||
        this.userFunction === funcoes['AGENTE-CONTRATACAO-SUBSTITUTO']
      );
    },
    canEncerrarFaseItem() {
      return this.isCrier || this.isAuthority || this.isAgenteContratacao;
    },
    qualifyingItem() {
      return this.items.some(item => item.estado === 'HABILITACAO');
    },
    judgingItem() {
      return this.items.some(item => item.estado === 'JULGAMENTO');
    },
    displayFinishQualification() {
      return (
        this.canEncerrarFaseItem &&
        this.logged &&
        this.company.id === this.auction.comprador.id &&
        this.verifySituation(SituacaoEnum.HABILITACAO) &&
        !this.qualifyingItem
      );
    },
    displayFinishJudgment() {
      return (
        this.canEncerrarFaseItem &&
        this.logged &&
        this.company.id === this.auction.comprador.id &&
        this.verifySituation(SituacaoEnum.JULGAMENTO) &&
        !this.judgingItem
      );
    },
    permissaoOperarProcesso() {
      const companyUser = this.user?.empresas?.find(
        empresaUsuario => empresaUsuario.id === this.company?.id,
      );

      if (!companyUser) return false;

      if (this.auction?.tipo === 'PREGAO') return Boolean(companyUser.operar_pregoes);
      if (this.auction?.tipo === 'CONCORRENCIA') return Boolean(companyUser.operar_concorrencias);
      if (this.auction?.tipo === 'DISPENSA') return Boolean(companyUser.operar_dispensas);

      return false;
    },
    happenedApproval() {
      return this.deadlines.some(deadline => deadline.prazo_id === 11); // houve habilitacao?
    },
    hasAnUnfinishedItem() {
      return !this.items.every(item => ['REVOGADO', 'DESERTO', 'FRACASSADA'].includes(item.estado));
    },
    displayAppealsTab() {
      if (
        [
          SituacaoEnum.INTERPOSICAO_DE_RECURSOS,
          SituacaoEnum.ADESAO_DE_PRECO,
          SituacaoEnum.ENCERRADA_COM_VENCEDOR,
          SituacaoEnum.ENCERRADA_COM_RECURSO,
        ].indexOf(this.situation) > -1
      ) {
        return true;
      }
      if (
        [SituacaoEnum.FRACASSADA, SituacaoEnum.REVOGADA, SituacaoEnum.SUSPENSA].indexOf(
          this.situation,
        ) > -1 &&
        this.appeals.length
      ) {
        return true;
      }
      return false;
    },
    displayCancelOrRevokeAuction() {
      return (
        this.loaded &&
        this.isAuthority &&
        [
          SituacaoEnum.EDITAL_PUBLICADO,
          SituacaoEnum.ENTREGA_DE_PROPOSTAS,
          SituacaoEnum.AGUARDANDO_INICIO_SESSAO,
          SituacaoEnum.ENCERRADA_COM_VENCEDOR,
          SituacaoEnum.ENCERRADA_COM_RECURSO,
          SituacaoEnum.SUSPENSA,
        ].indexOf(this.situation) > -1
      );
    },
    hasRequiredDocuments() {
      return this.requiredDocuments.length > 0;
    },
    displayDocumentsTab() {
      if (!this.hasRequiredDocuments) return false;
      const isNotDeserted = this.situation !== SituacaoEnum.DESERTA;
      const isAfterSessionStart = this.situation > SituacaoEnum.AGUARDANDO_INICIO_SESSAO;
      const hasPhaseInversion = !!this.auction?.phase_inversion;

      if (hasPhaseInversion && isAfterSessionStart && isNotDeserted) return true;

      const isProviderOrBidder = this.provider || this.isBidder;
      const canAccessOnProposalDelivery =
        this.proposalsDelivery &&
        isProviderOrBidder &&
        (!this.reservedNotice || this.company.preference);

      if (canAccessOnProposalDelivery) return true;

      const canAccessOnQualificationStage =
        this.situation >= SituacaoEnum.HABILITACAO && isNotDeserted && this.qualificationDeadline;

      return canAccessOnQualificationStage;
    },
    displayEndAnalysis() {
      return (
        this.verifySituation(SituacaoEnum.ANALISANDO_PROPOSTAS) &&
        this.isCrier &&
        this.logged &&
        this.company.id === this.auction.comprador.id
      );
    },
    displayFinishBids() {
      return (
        this.verifySituation(SituacaoEnum.LANCES) &&
        this.isCrier &&
        this.logged &&
        this.company.id === this.auction.comprador.id &&
        (this.auction.modo_disputa === 'ABERTO' || this.auction.modo_disputa === 'FECHADO_ABERTO')
      );
    },
    disableFinishSession() {
      return this.appeals.some(appeal => !isRecursoStatusTerminal(appeal.status));
    },
    todosRecursosEmStatusTerminal() {
      return (
        this.appeals.length > 0 &&
        this.appeals.every(appeal => isRecursoStatusTerminal(appeal.status))
      );
    },
    displayOpenSession() {
      const openingDate = new Date(this.auction.data_abertura);
      const isAfterOpeningDate =
        this.auction.data_abertura &&
        !Number.isNaN(openingDate.getTime()) &&
        new Date() >= openingDate;

      if (
        this.verifySituation(SituacaoEnum.AGUARDANDO_INICIO_SESSAO) &&
        this.isCrier &&
        this.logged &&
        this.company.id === this.auction.comprador.id &&
        this.items.some(item => item.estado === 'CADASTRADA') &&
        isAfterOpeningDate
      ) {
        return true;
      }
      return false;
    },
    displaySecondaryActionsMenu() {
      return (
        (this.loaded && this.activeAuction && this.provider) ||
        this.renderGetMinutes ||
        this.renderGetAccountIntegration ||
        (!this.isCrier && (this.proposals.length || this.proposalsDeleted.length) && this.logged) ||
        (this.isCrier && this.logged) ||
        (this.logged && this.allowedToChangeCrier) ||
        this.displayDefineRetorno ||
        this.displayChangeToSineDie ||
        this.displayReopenSession ||
        this.displayCancelOrRevokeAuction ||
        Boolean(this.opcaoEncerrarPrazoRecursal) ||
        (this.isBuyer && this.logged && this.permissaoOperarProcesso)
      );
    },
    opcaoEncerrarPrazoRecursal() {
      if (process.env.VUE_APP_ENABLE_ANTECIPACAO_PRAZO !== 'true') {
        return null;
      }

      if (this.situation !== 10 || !this.isCrier || !this.prazosRecursaisDefinidos) {
        return null;
      }

      const labels = {
        razoes: 'Encerrar Prazo Para Razões',
        contrarrazoes: 'Encerrar Prazo Para Contrarrazões',
      };

      const mensagensConfirmacao = {
        razoes:
          'Senhor(a) Agente de Contratação, tem certeza que deseja <strong>encerrar o prazo</strong> para razões?',
        contrarrazoes:
          'Senhor(a) Agente de Contratação, tem certeza que deseja <strong>encerrar o prazo</strong> para contrarrazões?',
      };

      const titulos = {
        razoes: 'Antecipar Razão',
        contrarrazoes: 'Antecipar Contrarrazão',
      };

      const janela = this.janelaRecursalVigente;
      if (!janela || janela === 'julgamento' || !labels[janela]) return null;

      return {
        janela,
        label: labels[janela],
        title: titulos[janela],
        mensagemConfirmacao: mensagensConfirmacao[janela],
      };
    },
    isSineDie() {
      return this.situation === SituacaoEnum.SUSPENSA && this.auction.suspensao_sine_die === true;
    },
    situacaoDisplayLabel() {
      if (this.isSineDie) return 'Suspensa Sine Die';
      const nome = this.auction.situacao?.nome || SituacaoLabel[this.auction.situacao?.id] || '';
      return this.situationName(nome);
    },
    exibirAjudaTourSituacao() {
      return this.opcoesAjudaContexto('auction.situation', this.ctxTourSituacao).length > 0;
    },
    ctxTourSituacao() {
      return {
        isCrier: this.isCrier,
        isBidder: this.isBidder,
        situation: this.situation,
        logged: this.logged,
        voltarEtapaVisivel: this.displayBackToProposals,
        auctionTypeName: this.auctionTypeName || this.auctionType,
        prazoManifestacaoVigente: this.prazoManifestacaoIntencaoVigente,
        hasAppealIntention: this.hasAppealIntention,
      };
    },
    chatTourAnchor() {
      if (this.situation === SituacaoEnum.AGUARDANDO_INICIO_SESSAO) {
        return 'aguardando-inicio-chat';
      }
      if (this.situation === SituacaoEnum.MANIFESTACAO_INTENCAO_RECURSO) {
        return 'manifestacao-intencao-chat';
      }
      return null;
    },
    displaySuspendSineDie() {
      if (!this.isCrier) return false;
      if (this.situation > 4 && this.situation < 12) return true;
      if (this.situation === SituacaoEnum.JULGAMENTO) return true;
      if (this.situation === SituacaoEnum.MANIFESTACAO_INTENCAO_RECURSO) return true;
      return false;
    },
    isSineDieFromEntregaDePropostas() {
      return (
        this.isSineDie &&
        this.auction.situacao_id_antes_suspensao === SituacaoEnum.ENTREGA_DE_PROPOSTAS
      );
    },
    displayDefineRetorno() {
      return (
        this.isCrier &&
        this.situation === SituacaoEnum.SUSPENSA &&
        !this.auction.data_reabertura &&
        (this.auction.suspensao_automatica || this.auction.suspensao_sine_die)
      );
    },
    displayChangeToSineDie() {
      return (
        (this.isCrier || this.isAgenteContratacao || this.isAuthority) &&
        this.situation === SituacaoEnum.SUSPENSA &&
        !this.isSineDie
      );
    },
    displayReopenSession() {
      return (
        this.isCrier &&
        this.situation === SituacaoEnum.SUSPENSA &&
        this.auction.data_reabertura &&
        !this.isSineDieFromEntregaDePropostas
      );
    },
    displaySuspendSineDieEDP() {
      return this.isCrier && this.situation === SituacaoEnum.ENTREGA_DE_PROPOSTAS;
    },
    displayPendingWarning() {
      return (
        this.proposalsDelivery &&
        ['CADASTRADA', 'INABILITADA'].indexOf(this.company.status) > -1 &&
        this.provider
      );
    },
    displayExtractProposals() {
      return true;
    },
    displayBackToProposals() {
      return (
        this.canRequestBackStage && !this.hasRunningStageClock && this.backStageOptions.length > 0
      );
    },
    canRequestBackStage() {
      return (
        this.isCrier &&
        this.logged &&
        this.company.id === this.auction.comprador.id &&
        [5, 6, 9, 10, 20].includes(this.situation) &&
        !(this.situation === SituacaoEnum.LANCES && this.closedDisputeMode && this.closedMode)
      );
    },
    hasRunningStageClock() {
      // Sit. 20: relógio da manifestação de intenção (mesmo bloqueio de Voltar Etapa que em Lances).
      if (this.situation === SituacaoEnum.MANIFESTACAO_INTENCAO_RECURSO) {
        return Boolean(this.prazoManifestacaoIntencaoVigente);
      }

      if (this.situation !== SituacaoEnum.LANCES) {
        return false;
      }

      // The timer component can still render 00:00 before bids actually start.
      // Consider clock running only when item states indicate an active timed phase.
      const runningItemStates = [
        'LANCE',
        'LANCE-RANDOMICO',
        'LANCE-FECHADO',
        'PREFERENCIA',
        'PREFERENCIA-INICIADA',
      ];

      return this.items.some(item => runningItemStates.includes(item.estado));
    },
    displaySuspend() {
      const existeItensEmLance = this.items.find(
        item =>
          ['LANCE', 'LANCE-RANDOMICO', 'LANCE-FECHADO', 'PREFERENCIA-INICIADA'].indexOf(
            item.estado,
          ) > -1,
      );
      if (this.isCrier && [6, 7].indexOf(this.situation) > -1 && existeItensEmLance) {
        return false;
      }

      if (
        this.isCrier &&
        this.situation === 11 &&
        this.items.some(item => item.estado === 'ADESAO')
      ) {
        return false;
      }

      // Como em Lances: não suspender enquanto o relógio da etapa estiver ativo.
      if (
        this.isCrier &&
        this.situation === SituacaoEnum.MANIFESTACAO_INTENCAO_RECURSO &&
        this.prazoManifestacaoIntencaoVigente
      ) {
        return false;
      }

      if (
        this.isCrier &&
        ((this.situation > 4 && this.situation < 12) ||
          this.situation === 19 ||
          this.situation === SituacaoEnum.MANIFESTACAO_INTENCAO_RECURSO)
      ) {
        return true;
      }

      if (this.isCrier && this.situation === SituacaoEnum.JULGAMENTO) {
        return true;
      }

      return false;
    },
    hasSession() {
      return this.deadlines.some(deadline => deadline.id === 7);
    },
    itemsForBids() {
      return this.items.some(item => ['CADASTRADA', 'LANCE'].indexOf(item.estado) > -1);
    },
    linkPdf() {
      const proportion = this.windowWidth / 10;
      return fileServer(
        `${this.getMinutesPdfName}#zoom=${proportion < 120 ? proportion : 120}&view=FitB`,
      );
    },
    noticeType() {
      if (this.auction.tipo_edital === 'AMPLO') {
        return 'Participação Ampla';
      }
      if (this.auction.tipo_edital === 'AMPLOCOTA') {
        return 'Quota Reservada para ME/EPP/MEI/Cooperativa c/ Preferência';
      }
      return 'Participação Exclusiva de ME/EPP/MEI/Cooperativa c/ Preferência';
    },
    getMinutesPdfName() {
      return (
        this.auction.ata ??
        sanitizeFileName(`ata-pe-${this.auction.comprador.id}-${this.auction.numero_oc}.pdf`)
      );
    },
    proposalsDelivery() {
      return this.situation === SituacaoEnum.ENTREGA_DE_PROPOSTAS;
    },
    provider() {
      return this.userFunction === funcoes.FORNECEDOR;
    },
    qualificationDeadline() {
      return this.deadlines.some(deadline => deadline.prazo_id === 11);
    },
    randomTime() {
      return this.items.some(item => item.estado === 'LANCE-RANDOMICO');
    },
    renderGetMinutes() {
      return (
        !this.auction.hasAta &&
        this.isCrier &&
        (this.verifySituation([
          SituacaoEnum.FRACASSADA,
          SituacaoEnum.DESERTA,
          SituacaoEnum.ENCERRADA_COM_VENCEDOR,
          SituacaoEnum.ENCERRADA_COM_RECURSO,
        ]) ||
          (this.verifySituation([SituacaoEnum.REVOGADA, SituacaoEnum.ANULADA]) && this.hasSession))
      );
    },
    renderGetAccountIntegration() {
      return (
        this.isCrier &&
        (this.verifySituation([
          SituacaoEnum.FRACASSADA,
          SituacaoEnum.DESERTA,
          SituacaoEnum.ENCERRADA_COM_VENCEDOR,
          SituacaoEnum.ENCERRADA_COM_RECURSO,
        ]) ||
          (this.verifySituation([SituacaoEnum.REVOGADA, SituacaoEnum.ANULADA]) && this.hasSession))
      );
    },
    requiredDocumentsSent() {
      const requiredTypes = this.requiredDocuments.map(element => element.type);
      const insertedTypes = this.auctionDocuments.map(element => element.type);
      return requiredTypes.every(type => insertedTypes.includes(type));
    },
    reservedNotice() {
      return this.auction.tipo_edital === 'RESERVADO';
    },
    situation() {
      return this.auction.situacao.id;
    },
    socketConnection() {
      return this.$socket.connected;
    },
  },
  watch: {
    items() {
      if (this.situation === 6) {
        const relogioParado = this.items.some(
          item => ['PRIMEIRA-CHAMADA', 'NOVA-CHAMADA'].indexOf(item.estado) > -1,
        );
        if (relogioParado) {
          this.buscarOfertaCompraLances({ ofertaCompraId: this.auction.id, tipo: 'empresa' });
        }
      }

      if (this.loaded && this.canRequestBackStage) {
        this.refreshBackStageOptionsAvailability();
      }

      this.$nextTick(() => {
        if (
          this.situation === 9 &&
          Object.keys(this.$refs.items.$refs).length &&
          this.$refs.items.$refs.myitems
        ) {
          this.$refs.items.$refs.myitems.verifyOverflowed();
        }
      });
    },
    situation(newSituation, oldSituation) {
      if (this.loaded) {
        // Drop stale destinations immediately so a previous stage's options
        // (e.g. only JULGAMENTO while in Habilitação) cannot survive the
        // transition while the async refresh is in flight.
        this.backStageOptions = [];
        this.refreshBackStageOptionsAvailability();
      }

      if (oldSituation === 17) {
        this.fetchChangingData(this.$route.params.id, true);
      }

      if (newSituation === 4 && this.loaded) {
        this.fetchChangingData(this.$route.params.id, true);
        this.selectedTab = 2;
      }

      if (newSituation === 5) {
        this.fetchRequiredDocuments(this.$route.params.id);

        if (this.isCrier || this.isAuthority) {
          this.fetchProposals({
            ofertaCompraId: this.auction.id,
            tipo: 'todas',
          });
          if (this.logged) {
            this.fetchDeletedProposals({
              ofertaCompraId: this.auction.id,
              tipo: this.isCrier ? 'deletadas-todas' : 'deletadas-empresa',
            });
          }
        }
      }

      if (newSituation === 8 && this.auction.divulgar_referencial) {
        this.buscarItens({ ofertaCompraId: this.auction.id, v2: this.$route.query.v2 });
      }

      if (newSituation !== 9 && oldSituation !== undefined) this.displayAuctionItems();
      if (newSituation === 9) {
        this.fetchChangingData(this.$route.params.id, true);
        this.fetchParticipants(this.auction.id);

        if (this.isCrier && this.adhesionStep) {
          this.buscarOfertaCompraLances({
            ofertaCompraId: this.auction.id,
            tipo: 'todas',
          });
        }
      }
      if (newSituation === 11 && this.isBidder) {
        this.getAuctionQualifications(this.auction.id);
        this.buscarOfertaCompraLances({ ofertaCompraId: this.auction.id, tipo: 'empresa' });
      }
      if (newSituation === SituacaoEnum.MANIFESTACAO_INTENCAO_RECURSO && this.auction.id) {
        this.buscarPrazoManifestacaoIntencao(this.auction.id).catch(() => {});
      }

      // Troca de fase: encerra tour/hint da situação anterior antes de avaliar a nova.
      if (oldSituation !== undefined && newSituation !== oldSituation) {
        this.ocultarHintsAtivos();
      }

      this.$nextTick(() => {
        this.tentarIniciarTourSituacao();
      });
    },
    janelaRecursalVigente() {
      if (this.loaded && this.situation === 10 && this.canRequestBackStage) {
        this.refreshBackStageOptionsAvailability();
      }
    },
    isCrier() {
      this.$nextTick(() => {
        this.tentarIniciarTourSituacao();
      });
    },
    prazosRecursaisDefinidos() {
      this.$nextTick(() => {
        this.tentarIniciarTourSituacao();
      });
    },
    displayBackToProposals() {
      this.$nextTick(() => {
        this.tentarIniciarTourSituacao();
      });
    },
    prazoManifestacaoIntencaoVigente() {
      this.$nextTick(() => {
        this.tentarIniciarTourSituacao();
      });
    },
    prazoManifestacaoIntencaoDefinido() {
      this.$nextTick(() => {
        this.tentarIniciarTourSituacao();
      });
    },
    canAccessArpTab(val) {
      if (val) {
        this.$nextTick(() => {
          this.tentarIniciarTourArp();
        });
      }
    },
    socketConnection() {
      if (this.loaded) {
        if (!this.socketConnection) {
          this.needReload = true;
          this.reconnectSocket();
        } else if (this.socketConnection) {
          this.fetchChangingData(this.$route.params.id, true);
          this.needReload = false;
        }
      }
    },
  },
  sockets: {
    'auction:items-after-remove': function updateItemsAfterRemove(items) {
      this.updateItemsAfterRemove(items);
    },
    'ofertacompra:itens': function atualizarOfertaCompraItens(items) {
      this.atualizarItens(items);
    },
    'ofertacompra:itens-estado': function atualizarOfertaCompraItensEstado(items) {
      this.atualizarItensEstado(items);
    },
    'ofertacompra:atualizar': async function atualizarOfertaCompra(auction) {
      const previousSituation = this.situation;
      this.atualizarOfertaCompra(auction);
      const nextSituation = auction?.situacao?.id ?? auction?.situacaoId;
      if (
        nextSituation === SituacaoEnum.ADESAO_DE_PRECO &&
        previousSituation !== SituacaoEnum.ADESAO_DE_PRECO &&
        this.company.id === this.auction.comprador.id
      ) {
        await this.buscarAdesoesPregao();
      }
    },
    'ofertacompra:proposta-adicionar': function adicionarProposta(proposal) {
      this.AUCTION_PROPOSALS(proposal);
    },
    'ofertacompra:proposta-excluir': function excluirProposta(proposal) {
      this.AUCTION_PROPOSALS_REMOVE(proposal);
    },
    'ofertacompra:lance-excluir': function excluirLances(lances) {
      this.OFERTA_COMPRA_LANCES_DELETAR(lances);
    },
    'ofertacompra:proposta': async function atualizarOfertaCompraProposta(data) {
      if (this.situation === 5 || 'readequada' in data) {
        this.AUCTION_PROPOSALS_UPDATE(data);
      }
      this.updateAuctionBidsClassification(data);
    },
    'ofertacompra:lances-empresa': function atualizarOfertaCompraLancesEmpresa(bids) {
      if (bids.length && this.isBidder && this.bids[0].fornecedor_id === bids[0].fornecedor_id) {
        this.atualizarLancesAdesaoEmpresa(bids);
      }
    },
    'ofertacompra:lances-habilitacao': function atualizarOfertaCompraLancesHabilitacao(updates) {
      this.atualizarHabilitacaoLances(updates);
    },
    'ofertacompra:elegiveis-fechado': function atualizarDadosElegiveisFechado(dados) {
      this.atualizarElegiveisFechado(dados);
    },
    'ofertacompra:participantes-fechado': function atualizarDadosParticipantesFechado(dados) {
      this.atualizarParticipantesFechado(dados);
    },
    'ofertacompra:prazos': function atualizarOfertaCompraPrazos(deadlines) {
      this.atualizarPrazos(deadlines);
    },
    'ofertacompra:recursos': function atualizarOfertaCompraRecursos(appeals) {
      this.atualizarRecursos(appeals);
    },
    'ofertacompra:prazos-recursais': function atualizarPrazosRecursais(payload) {
      this.atualizarPrazosRecursais(payload);
    },
    // API emite singular (`prazo-...`); manter alias plural por compatibilidade.
    'ofertacompra:prazo-manifestacao-intencao': function onPrazoManifestacaoIntencao(payload) {
      this.atualizarPrazoManifestacaoIntencao(payload);
    },
    'ofertacompra:prazos-manifestacao-intencao': function onPrazosManifestacaoIntencao(payload) {
      this.atualizarPrazoManifestacaoIntencao(payload);
    },
    'ofertacompra:change-crier': async function changeCrierHandler() {
      const wasCrier = this.isCrier;

      try {
        await this.buscarOfertaCompraResponsaveis(this.auction.id);
      } catch (error) {
        return;
      }

      if (wasCrier && !this.isCrier) {
        this.alterarPregoeiroModal = false;
        this.selectedNovoPregoeiroUser = null;
        this.responsavelAlterarPregoeiro = '';
        this.$alerta('Você não é mais o responsável por operar este processo.', 'is-warning');
      }
    },
    'auction:judgment-diligences': function updateJudgmentDiligences(diligences) {
      this.updateJudgmentDiligences(diligences);
      if (this.auction?.id) {
        this.fetchUnresolvedJudgmentDiligences({ auctionId: this.auction.id });
      }
    },
    'auction:documents': function updateAcceptedDocuments(documents) {
      this.updateAuctionDocuments(documents);
      if (this.auction?.id && !this.proposalsDelivery) {
        this.fetchAuctionDocumentsList({
          auctionId: this.auction.id,
          filters: { page: 1, perPage: 20 },
        });
      }
    },
  },
  filters: {
    formatDate,
  },
  methods: {
    purchasingUnitLabel(unit) {
      if (!unit) return 'Unidade não definida';
      return [unit.code, unit.name].filter(Boolean).join(' — ') || 'Unidade não definida';
    },
    purchasingUnitStatusLabel(unit) {
      const labels = {
        INACTIVE: 'Inativa',
        INATIVA: 'Inativa',
        CANCELADO: 'Cancelada',
        PENDING: 'Sincronização pendente',
        PENDENTE: 'Sincronização pendente',
        PROCESSING: 'Sincronizando',
        PROCESSANDO: 'Sincronizando',
        ERROR: 'Erro de sincronização',
        ERRO: 'Erro de sincronização',
      };
      return [labels[unit?.status], labels[unit?.syncStatus]]
        .filter(Boolean)
        .filter((value, index, values) => values.indexOf(value) === index)
        .join(' · ');
    },
    purchasingUnitStatusType(unit) {
      const label = this.purchasingUnitStatusLabel(unit);
      return label.includes('Erro') || label.includes('Cancelada') ? 'is-danger' : 'is-warning';
    },
    ...mapActions('auctionsClarifications', ['getAuctionClarifications']),
    ...mapActions('auctionsDeclarations', ['cleanAuctionDeclarations', 'searchDeclarations']),
    ...mapActions('auctionsDocuments', [
      'fetchAuctionBidderDocuments',
      'cleanAuctionDocuments',
      'fetchAuctionDocumentsList',
      'updateAuctionDocuments',
    ]),
    ...mapActions('judgmentDiligences', [
      'fetchJudgmentDiligences',
      'fetchUnresolvedJudgmentDiligences',
      'updateJudgmentDiligences',
    ]),
    ...mapActions('auctionsImpugnations', ['getAuctionImpugnations']),
    ...mapActions('auctionsOptions', ['fetchAuctionWatch', 'updateAuctionWatch']),
    ...mapActions('auctionsQualifications', ['getAuctionQualifications']),
    ...mapActions('auctionsBidders', ['fetchUserLoggedBidder']),
    ...mapActions('chat', ['fetchMessages', 'fetchParticipants']),
    ...mapActions('companiesDocuments', ['fetchCompanyDocuments']),
    ...mapActions('documentTypes', ['fetchDocumentTypes']),
    ...mapActions('requiredDocuments', ['fetchRequiredDocuments']),
    ...mapActions('proposals', [
      'cleanAuctionProposals',
      'fetchProposals',
      'fetchDeletedProposals',
      'updateProposalForBidder',
      'updateProposalForBuyer',
      'removeProposal',
      'generateExtractProposals',
    ]),
    ...mapActions([
      'atualizarItens',
      'atualizarItensEstado',
      'atualizarOfertaCompra',
      'atualizarPrazos',
      'atualizarElegiveisFechado',
      'atualizarParticipantesFechado',
      'atualizarLancesAdesaoEmpresa',
      'atualizarHabilitacaoLances',
      'atualizarRecursos',
      'atualizarPrazosRecursais',
      'buscarPrazosRecursais',
      'salvarPrazosRecursais',
      'encerrarPrazoRecursal',
      'atualizarPrazoManifestacaoIntencao',
      'buscarPrazoManifestacaoIntencao',
      'salvarPrazoManifestacaoIntencao',
      'encerrarPrazoManifestacaoIntencao',
      'buscarOfertaCompra',
      'buscarItens',
      'buscarOfertaCompraPrazos',
      'buscarOfertaCompraAnexos',
      'buscarOfertaCompraResponsaveis',
      'buscarOfertaCompraLances',
      'buscarOfertaCompraIntencaoRecurso',
      'sincronizarIntencaoRecursoPorSituacao',
      'buscarOfertaCompraElegiveisFechado',
      'buscarOfertaCompraParticipantesFechado',
      'buscarOfertaCompraRecursos',
      'buscarAdesoesPregao',
      'buscarOfertaCompraAtasPrecosEligibility',
      'cancelarArpJourney',
      'cancelAuction',
      'encerrarAnalisePropostas',
      'encerrarEtapaLances',
      'encerrarEtapaHabilitacao',
      'encerrarEtapaJulgamento',
      'voltarParaAnaliseDePropostas',
      'obterOpcoesVoltarEtapa',
      'voltarEtapa',
      'encerrarInterposicaoDeRecursos',
      'encerrarPregao',
      'generateAuctionMinutes',
      'invalidatePin',
      'limparPregao',
      'limparAnexosPregao',
      'limparItensPregao',
      'limparResponsaveisPregao',
      'limparLancesPregao',
      'openAuctionSession',
      'reabrirOfertaCompra',
      'revokeAuction',
      'suspendAuction',
      'changeSuspensionToSineDie',
      'updateAuctionBidsClassification',
      'updateItemsAfterRemove',
      'updateReopeningDate',
      'exportAccountIntegration',
      'exportPublicenterIntegration',
    ]),
    ...mapMutations('proposals', [
      'AUCTION_PROPOSALS_REMOVE',
      'AUCTION_PROPOSALS',
      'AUCTION_PROPOSALS_UPDATE',
    ]),
    ...mapMutations(['OFERTA_COMPRA_LANCES_DELETAR']),
    async cancelElectronicAuction(dados) {
      this.loading.revoke = true;
      try {
        await this.cancelAuction(dados);
        this.$alerta(`${this.auctionType} anulado`, 'is-success');
      } catch (error) {
        this.$alerta(`Erro ao anular ${this.auctionType}`, 'is-danger');
      }
      this.loading.revoke = false;
      this.$refs.justificationModal.close();
    },
    closeBatchSender() {
      this.display.batchItems = false;
      this.display.items = true;
    },
    closeReadjustProposals() {
      this.display.readjustProposals = { open: false, proposal: {} };
      this.display.items = true;
    },
    displayDetails({ tipo, item, ofertaCompraId, proposal }) {
      this.auctionId = ofertaCompraId;
      this.item = item;
      if (tipo === 'oferta') {
        this.display.items = false;
        this.display.offers = true;
      } else if (tipo === 'oferta-lote') {
        this.display.batchItems = true;
        this.display.items = false;
      } else if (tipo === 'readequacao-proposta') {
        this.display.readjustProposals = { open: true, proposal };
        this.display.items = false;
      }
    },
    goToAttachments() {
      const query =
        this.isSineDieFromEntregaDePropostas || this.auction.situacao.id <= 3 ? 'dados' : 'anexos';
      window.location.href = `${window.location.origin}/ofertascompra/editar/${this.auction.id}/${query}`;
    },
    funcaoNomeAlterarPregoeiro(value) {
      const funcao = this.funcoesAlterarPregoeiro.find(f => f.enum === value);
      if (funcao) return funcao.nome;
      return 'Função inválida';
    },
    podeSerNovoPregoeiro(user) {
      if (!user || !this.responsaveis.length) return true;
      const responsaveisDoUsuario = this.responsaveis.filter(
        r =>
          r.usuario_id === user.id ||
          (r.email && user.userEmail && r.email.toLowerCase() === user.userEmail.toLowerCase()),
      );
      if (!responsaveisDoUsuario.length) return true;
      return responsaveisDoUsuario.some(r =>
        ['AGENTE-CONTRATACAO', 'PREGOEIRO-SUBSTITUTO', 'AGENTE-CONTRATACAO-SUBSTITUTO'].includes(
          r.funcao,
        ),
      );
    },
    onSelectNovoPregoeiroUser(option) {
      if (!this.podeSerNovoPregoeiro(option)) {
        this.$alerta(
          'Este usuário não pode ser selecionado. Apenas usuários que não são responsáveis no processo, ou que sejam Agente de Contratação ou Pregoeiro Substituto.',
          'is-danger',
        );
        this.selectedNovoPregoeiroUser = null;
        this.responsavelAlterarPregoeiro = '';
        return;
      }
      this.selectedNovoPregoeiroUser = option;
    },
    async confirmarAlterarPregoeiro() {
      if (!this.selectedNovoPregoeiroUser) return;

      this.loading.confirmarAlterarPregoeiro = true;
      try {
        await changeCrier(this.auction.id, this.selectedNovoPregoeiroUser.id);
        this.$alerta('Alterado com sucesso.', 'is-success');
        this.alterarPregoeiroModal = false;
        this.selectedNovoPregoeiroUser = null;
        this.responsavelAlterarPregoeiro = '';
      } catch (error) {
        this.$alerta('Erro ao alterar.', 'is-danger');
      } finally {
        this.loading.confirmarAlterarPregoeiro = false;
      }
    },
    pesquisarAlterarPregoeiro: debounce(async function obterPesquisaAlterarPregoeiro() {
      this.pesquisandoAlterarPregoeiro = true;
      const termo = this.responsavelAlterarPregoeiro?.trim?.();
      const companyId = this.auction?.comprador?.id ?? this.company?.id;
      if (termo && !this.selectedNovoPregoeiroUser && companyId) {
        try {
          const { data: searchUsers } = await search(companyId, termo);
          const usuarios = searchUsers.data ?? [];
          this.pesquisaAlterarPregoeiro = usuarios.filter(u => this.podeSerNovoPregoeiro(u));
        } catch (error) {
          this.$alerta('Erro ao pesquisar usuários', 'is-danger');
          this.pesquisaAlterarPregoeiro = [];
        }
      } else {
        this.pesquisaAlterarPregoeiro = [];
      }
      this.pesquisandoAlterarPregoeiro = false;
    }, 500),
    async openAlterarPregoeiroModal() {
      this.selectedNovoPregoeiroUser = null;
      this.responsavelAlterarPregoeiro = '';
      this.pesquisaAlterarPregoeiro = [];
      this.alterarPregoeiroModal = true;
      this.loading.alterarPregoeiro = true;
      try {
        await this.buscarOfertaCompraResponsaveis(this.auction.id);
      } catch (error) {
        this.$alerta('Erro ao carregar responsáveis', 'is-danger');
      } finally {
        this.loading.alterarPregoeiro = false;
      }
    },
    async generateProposalsExtractHandler() {
      this.loading.general = true;
      this.loading.generateExtract = true;
      try {
        const auctionId = this.auction.id;

        const fileName = await this.generateExtractProposals(auctionId);
        const url = fileServer(fileName);
        window.open(url, '_blank');
        this.$alerta('Extrato gerado com sucesso.', 'is-success');
      } catch (error) {
        this.$alerta('Erro ao gerar o extrato.', 'is-danger');
      } finally {
        this.loading.generateExtract = false;
        this.loading.general = false;
      }
    },
    goToBilling() {
      this.$router.push({ name: 'CompanyBillingMethod', params: { id: this.company.id } });
    },
    displaySendDocuments({ item, company }) {
      this.resetJudgmentDiligenceViews();
      this.display.items = false;
      this.display.sendDocuments = true;
      this.item = item;
      this.qualificationCompany = company;
    },
    displayJudgmentDiligenceUpload({ item, company, diligence }) {
      this.resetJudgmentDiligenceViews();
      this.display.items = false;
      this.display.judgmentDiligenceUpload = true;
      this.item = item;
      this.qualificationCompany = company;
      this.judgmentDiligence = diligence;
    },
    displayJudgmentDiligenceReview({ item, company, diligence }) {
      this.resetJudgmentDiligenceViews();
      this.display.items = false;
      this.display.judgmentDiligenceReview = true;
      this.item = item;
      this.qualificationCompany = company;
      this.judgmentDiligence = diligence;
    },
    resetJudgmentDiligenceViews() {
      this.display.sendDocuments = false;
      this.display.judgmentDiligenceUpload = false;
      this.display.judgmentDiligenceReview = false;
      this.judgmentDiligence = null;
    },
    displayAuctionItems() {
      this.display.items = true;
      this.display.offers = false;
      this.resetJudgmentDiligenceViews();
      this.buscarItens({ ofertaCompraId: this.auction.id, v2: this.$route.query.v2 });
    },
    reconnectSocket() {
      this.socketResource.closeConnection();
      this.socketResource.openConnection();
    },
    enterAuctionRoom() {
      if (!this.logging) {
        this.socketResource.enterAuction(this.auction.id, this.user.id, this.company.id);
      }
    },
    async finishProposalsAnalysis() {
      this.loading.general = true;
      try {
        await this.encerrarAnalisePropostas();
        this.$alerta('Análise de propostas encerrada', 'is-success');
        this.selectedTab = this.$refs.tabs.activeItem.index;
      } catch (error) {
        this.$alerta('Erro ao encerrar análise de propostas', 'is-danger');
      }
      this.loading.general = false;
    },
    async getAccountIntegration() {
      this.loading.exportAccountIntegration = true;
      try {
        await this.exportAccountIntegration(this.$route.params.id);
      } catch (error) {
        this.$alerta('Erro ao exportar arquivo', 'is-danger');
      }
      this.loading.exportAccountIntegration = false;
    },
    async getPublicenterIntegration() {
      this.loading.exportPublicenterIntegration = true;
      try {
        await this.exportPublicenterIntegration(this.$route.params.id);
      } catch (error) {
        this.$alerta('Erro ao exportar arquivo', 'is-danger');
      }
      this.loading.exportPublicenterIntegration = false;
    },
    async finishBids() {
      const confirmation = await this.$confirmacao({
        message: 'Essa ação irá encerrar a etapa de Lances. Deseja prosseguir?',
        cancelText: 'Cancelar',
        confirmText: 'Continuar',
        type: 'is-info',
        hasIcon: true,
      });
      if (!confirmation) {
        return;
      }

      this.loading.general = true;
      try {
        await this.encerrarEtapaLances();
        this.$alerta('Etapa de lances encerrada', 'is-success');
      } catch (error) {
        this.$alerta('Erro ao encerrar etapa de lances', 'is-danger');
      }
      this.loading.general = false;
    },
    async finishQualification() {
      const qualificationStageLabel = this.isAdhesionQualificationContext
        ? 'Habilitação da Adesão'
        : 'Habilitação';
      const confirmation = await this.$confirmacao({
        message: `Essa ação irá encerrar a etapa de ${qualificationStageLabel}. Deseja prosseguir?`,
        cancelText: 'Cancelar',
        confirmText: 'Continuar',
        type: 'is-info',
        hasIcon: true,
      });
      if (!confirmation) {
        return;
      }

      this.loading.general = true;
      try {
        await this.encerrarEtapaHabilitacao();
        this.$alerta('Etapa de habilitação encerrada', 'is-success');
      } catch (error) {
        this.$alerta(
          error?.response?.data?.error || 'Erro ao encerrar etapa de habilitação',
          'is-danger',
        );
      } finally {
        this.loading.general = false;
      }
    },
    async finishJudgment() {
      const confirmation = await this.$confirmacao({
        message: 'Essa ação irá encerrar a etapa de Julgamento. Deseja prosseguir?',
        cancelText: 'Cancelar',
        confirmText: 'Continuar',
        type: 'is-info',
        hasIcon: true,
      });
      if (!confirmation) {
        return;
      }

      this.loading.general = true;
      try {
        await this.encerrarEtapaJulgamento();
        this.$alerta('Etapa de julgamento encerrada', 'is-success');
      } catch (error) {
        this.$alerta(
          error?.response?.data?.error || 'Erro ao encerrar etapa de julgamento',
          'is-danger',
        );
      } finally {
        this.loading.general = false;
      }
    },
    getStageLabel(destino) {
      const labels = {
        ANALISE_PROPOSTAS: 'Análise de Propostas',
        HABILITACAO: 'Habilitação',
        HABILITACAO_ADESAO: 'Habilitação da Adesão',
        JULGAMENTO: 'Julgamento',
      };

      return labels[destino] || destino;
    },
    async selectBackStageOption(options) {
      if (options.length === 1) {
        return options[0];
      }
      return this.openBackStageSelection(options);
    },
    openBackStageSelection(options) {
      return new Promise(resolve => {
        this.backStageSelectionResolver = resolve;
        this.backStageSelection.options = options;
        this.backStageSelection.selectedDestino = options[0]?.destino || '';
        this.backStageSelection.active = true;
      });
    },
    resetBackStageSelection() {
      this.backStageSelection.active = false;
      this.backStageSelection.options = [];
      this.backStageSelection.selectedDestino = '';
      this.backStageSelectionResolver = null;
    },
    onBackStageSelectionCancel() {
      if (typeof this.backStageSelectionResolver === 'function') {
        this.backStageSelectionResolver(null);
      }
      this.resetBackStageSelection();
    },
    confirmBackStageSelection() {
      const selectedOption = this.backStageSelection.options.find(
        option => option.destino === this.backStageSelection.selectedDestino,
      );
      if (typeof this.backStageSelectionResolver === 'function') {
        this.backStageSelectionResolver(selectedOption || null);
      }
      this.resetBackStageSelection();
    },
    async goBackToPreviousStage() {
      let options = [];
      try {
        // Always refetch: cached options can lag behind situacaoId after a
        // stage transition (items/situation refresh race).
        const data = await this.obterOpcoesVoltarEtapa();
        options = data?.opcoes || [];
        this.backStageOptions = options;

        if (!options.length) {
          this.$alerta('Não há etapa anterior disponível para retorno', 'is-warning');
          return;
        }
      } catch (error) {
        const errorMessage = error?.data?.message || error?.data?.error || error?.message;
        this.$alerta(errorMessage || 'Erro ao voltar etapa', 'is-danger');
        return;
      }

      const selectedOption = await this.selectBackStageOption(options);
      if (!selectedOption) {
        return;
      }

      const selectedLabel = this.getStageLabel(selectedOption.destino);
      const confirmation = await this.$confirmacao({
        message: `Essa ação irá voltar para a etapa de ${selectedLabel}. Deseja prosseguir?`,
        cancelText: 'Cancelar',
        confirmText: 'Continuar',
        type: 'is-info',
        hasIcon: true,
      });

      if (!confirmation) {
        return;
      }

      this.loading.general = true;
      try {
        await this.voltarEtapa({
          destino: selectedOption.destino,
        });
        this.$alerta(`Processo retornado para ${selectedLabel}`, 'is-success');
      } catch (error) {
        const errorMessage = error?.data?.message || error?.data?.error || error?.message;
        this.$alerta(errorMessage || 'Erro ao voltar etapa', 'is-danger');
      } finally {
        this.loading.general = false;
      }
    },
    async refreshBackStageOptionsAvailability() {
      const requestId = this.backStageOptionsRequestId + 1;
      this.backStageOptionsRequestId = requestId;

      if (!this.canRequestBackStage) {
        if (requestId === this.backStageOptionsRequestId) {
          this.backStageOptions = [];
        }
        return;
      }

      try {
        const data = await this.obterOpcoesVoltarEtapa();
        if (requestId !== this.backStageOptionsRequestId) {
          return;
        }
        this.backStageOptions = data?.opcoes || [];
      } catch (error) {
        if (requestId !== this.backStageOptionsRequestId) {
          return;
        }
        this.backStageOptions = [];
      }
    },
    async startBiddingStep(auctionId) {
      this.loading.general = true;
      try {
        await this.encerrarInterposicaoDeRecursos(auctionId);
        this.$alerta('Iniciando etapa de Lances', 'is-success');
      } catch (error) {
        this.$alerta('Erro ao iniciar etapa de Lances', 'is-danger');
      }
      this.loading.general = false;
    },
    async finishSession(auctionId) {
      if (this.disableFinishSession) {
        this.$alerta(
          'Todos os recursos devem estar concluídos antes de encerrar a sessão',
          'is-danger',
        );
        return;
      }

      const confirmation = await this.$confirmacao({
        message: `Essa ação irá encerrar o(a) ${this.auctionType}. Deseja prosseguir?`,
        cancelText: 'Cancelar',
        confirmText: 'Continuar',
        type: 'is-warning',
        hasIcon: true,
      });
      if (!confirmation) {
        return;
      }

      this.loading.finishSession = true;
      try {
        await this.encerrarPregao(auctionId);

        this.$alerta(`${this.auctionType} encerrado`, 'is-success');
      } catch (error) {
        this.$alerta(`Erro ao encerrar ${this.auctionType}`, 'is-danger');
      }
      this.loading.finishSession = false;
    },
    async fetchAuction(auctionId) {
      this.loading.fetchAuction = true;

      try {
        try {
          await this.buscarOfertaCompra(auctionId);
        } catch (error) {
          this.$router.push({ name: 'PaginaInexistente' });
          return;
        }

        await this.buscarOfertaCompraResponsaveis(auctionId);

        // Eligibility da aba ARP não deve depender do restante do fetch (ex.: prazos vazios).
        const eligibilityPromise = this.buscarOfertaCompraAtasPrecosEligibility(auctionId).catch(
          () => null,
        );

        await this.fetchChangingData(auctionId);

        if (this.activeAuction && (this.provider || this.isBidder)) {
          await this.fetchAuctionWatch({ auctionId });
        }

        await Promise.all([
          this.getAuctionClarifications(auctionId),
          this.getAuctionImpugnations(auctionId),
          this.buscarOfertaCompraAnexos(auctionId),
          eligibilityPromise,
        ]);

        await this.refreshBackStageOptionsAvailability();
        this.selectTab(this.$route);
      } finally {
        this.loading.fetchAuction = false;
      }
    },
    async fetchChangingData(auctionId, reloadAuction = false) {
      if (reloadAuction) {
        await Promise.all([this.buscarOfertaCompra(auctionId), this.fetchMessages(auctionId)]);
        await this.enterAuctionRoom();
        setTimeout(() => {
          this.fetchParticipants(auctionId);
        }, 100);
      }
      await Promise.all([
        this.buscarItens({ ofertaCompraId: auctionId, v2: this.$route.query.v2 }),
        this.buscarOfertaCompraPrazos(auctionId),
      ]);

      if (
        [SituacaoEnum.ENTREGA_DE_PROPOSTAS, SituacaoEnum.AGUARDANDO_INICIO_SESSAO].indexOf(
          this.situation,
        ) > -1 &&
        (this.provider || this.isBidder)
      ) {
        await Promise.all([
          this.searchDeclarations({
            auctionId: this.auction.id,
            companyId: this.company.id,
          }),
          this.fetchRequiredDocuments(auctionId),
        ]);
      }

      if (this.situation >= SituacaoEnum.ANALISANDO_PROPOSTAS) {
        await this.fetchRequiredDocuments(auctionId);
      }
      if (this.situation >= SituacaoEnum.ENTREGA_DE_PROPOSTAS) {
        await this.fetchProposals({
          ofertaCompraId: auctionId,
          tipo: 'todas',
        });
        if (this.logged) {
          await this.fetchDeletedProposals({
            ofertaCompraId: auctionId,
            tipo: this.isCrier ? 'deletadas-todas' : 'deletadas-empresa',
          });
        }
      }

      if (this.situation >= SituacaoEnum.LANCES) {
        if (this.situation === SituacaoEnum.LANCES && this.closedMode) {
          await this.buscarOfertaCompraElegiveisFechado();
        }

        if (this.isCrier) {
          await this.buscarOfertaCompraLances({
            ofertaCompraId: auctionId,
            tipo: 'todas',
          });
        }

        if (this.isBidder) {
          await this.buscarOfertaCompraLances({
            ofertaCompraId: auctionId,
            tipo: 'empresa',
          });
        }
      }

      if (
        [SituacaoEnum.NEGOCIACAO, SituacaoEnum.HABILITACAO, SituacaoEnum.LANCES].indexOf(
          this.situation,
        ) > -1 &&
        this.closedDisputeMode
      ) {
        await this.buscarOfertaCompraParticipantesFechado();
      }
      if (
        this.auction.tipo_pregao === 'REGISTRO' &&
        this.situation >= SituacaoEnum.HABILITACAO &&
        this.isBidder
      ) {
        await this.getAuctionQualifications(auctionId);
      }
      if (
        [
          SituacaoEnum.HABILITACAO,
          SituacaoEnum.JULGAMENTO,
          SituacaoEnum.MANIFESTACAO_INTENCAO_RECURSO,
        ].includes(this.situation) &&
        this.isBidder
      ) {
        await this.sincronizarIntencaoRecursoPorSituacao({ ofertaCompraId: auctionId });
      }
      if (this.situation === SituacaoEnum.MANIFESTACAO_INTENCAO_RECURSO) {
        try {
          await this.buscarPrazoManifestacaoIntencao(auctionId);
        } catch (error) {
          // Prazo ainda não definido — a UI trata como não definido
        }
      }
      if (
        this.situation === SituacaoEnum.ADESAO_DE_PRECO &&
        this.company.id === this.auction.comprador.id
      ) {
        await this.buscarAdesoesPregao();
      }
      if (
        [
          SituacaoEnum.INTERPOSICAO_DE_RECURSOS,
          SituacaoEnum.ADESAO_DE_PRECO,
          SituacaoEnum.FRACASSADA,
          SituacaoEnum.REVOGADA,
          SituacaoEnum.ENCERRADA_COM_VENCEDOR,
          SituacaoEnum.ENCERRADA_COM_RECURSO,
          SituacaoEnum.SUSPENSA,
        ].indexOf(this.situation) > -1
      ) {
        if (this.isBidder) {
          await this.buscarOfertaCompraIntencaoRecurso(auctionId);
        }
        await this.buscarOfertaCompraRecursos(auctionId);
        await this.buscarPrazosRecursais(auctionId);
      }
    },
    async abrirDialogoPrazosRecursais(ajuste = false) {
      this.dialogoPrazosRecursaisAjuste = ajuste;

      if (ajuste && this.auction && this.auction.id) {
        await this.buscarPrazosRecursais(this.auction.id);
      }

      await this.$nextTick();
      this.dialogoPrazosRecursais = true;
    },
    async confirmarPrazosRecursais(dados) {
      if (this.dialogoPrazosRecursaisAjuste) {
        this.dialogoPrazosRecursais = false;
        this.abrirJustificativaPrazosRecursais('ajustePrazosRecursais', dados);
        return;
      }

      this.loadingPrazosRecursais = true;
      try {
        await this.salvarPrazosRecursais(dados);
        this.dialogoPrazosRecursais = false;
        this.$alerta('Prazos recursais definidos com sucesso', 'is-success');
      } catch (error) {
        const mensagem = error?.response?.data?.message || 'Erro ao definir prazos recursais';
        this.$alerta(mensagem, 'is-danger');
      }
      this.loadingPrazosRecursais = false;
    },
    async abrirDialogoPrazoManifestacaoIntencao() {
      if (this.auction?.id) {
        try {
          await this.buscarPrazoManifestacaoIntencao(this.auction.id);
        } catch (error) {
          // Prazo ainda não definido — segue com limites padrão
        }
      }
      await this.$nextTick();
      this.dialogoPrazoManifestacaoIntencao = true;
    },
    async confirmarPrazoManifestacaoIntencao(dados) {
      this.loadingPrazoManifestacaoIntencao = true;
      try {
        await this.salvarPrazoManifestacaoIntencao(dados);
        this.dialogoPrazoManifestacaoIntencao = false;
        this.$alerta('Relógio da manifestação iniciado com sucesso', 'is-success');
      } catch (error) {
        const mensagem =
          error?.response?.data?.message || 'Erro ao iniciar relógio da manifestação';
        this.$alerta(mensagem, 'is-danger');
      }
      this.loadingPrazoManifestacaoIntencao = false;
    },
    async confirmarAvancarInterposicaoRecursos() {
      const confirmacao = await this.$confirmacao({
        message:
          'Deseja <strong>encerrar a Manifestação de Intenção de Recurso</strong>?<br><br>Os licitantes não poderão mais manifestar intenção de recurso nesta etapa.',
        cancelText: 'Cancelar',
        confirmText: 'Encerrar Manifestação',
        type: 'is-warning',
        hasIcon: true,
      });
      if (!confirmacao) return;

      this.loadingPrazoManifestacaoIntencao = true;
      try {
        await this.encerrarPrazoManifestacaoIntencao({});
        this.atualizarOfertaCompra({
          situacao: {
            id: SituacaoEnum.INTERPOSICAO_DE_RECURSOS,
            nome: SituacaoLabel[SituacaoEnum.INTERPOSICAO_DE_RECURSOS],
          },
        });
        this.$alerta('Manifestação encerrada', 'is-success');
      } catch (error) {
        const mensagem =
          error?.response?.data?.message || 'Não foi possível encerrar a Manifestação';
        this.$alerta(mensagem, 'is-danger');
      } finally {
        this.loadingPrazoManifestacaoIntencao = false;
      }
    },
    abrirJustificativaPrazosRecursais(action, payload) {
      this.action = action;
      this.justification.pendingPayload = payload;
      this.justification.displayDateTime = false;
      this.justification.displaySuspendText = false;
      this.justification.displayWorkingDays = false;
      this.justification.displaySuccess = false;
      this.justification.displayFail = true;
      this.justification.failText = 'Confirmar';
      this.justification.skipDoubleConfirm = true;
      this.justification.titleText = payload?.titleText || 'Justificativa';
      this.justification.placeholderText = payload?.placeholderText || '';
      this.justification.introText = payload?.introText || '';
      this.justification.active = true;
    },
    confirmarEncerrarPrazoRecursal() {
      const opcao = this.opcaoEncerrarPrazoRecursal;
      if (!opcao) return;

      this.abrirJustificativaPrazosRecursais('encerrarPrazoRecursal', {
        janela: opcao.janela,
        titleText: opcao.title,
        placeholderText: 'Justificativa',
        introText: opcao.mensagemConfirmacao,
      });
    },
    async executarEncerrarPrazoRecursal(janela, justificativa) {
      this.loadingPrazosRecursais = true;
      try {
        await this.encerrarPrazoRecursal({ janela, justificativa });
        this.$alerta('Prazo recursal encerrado com sucesso', 'is-success');
        this.$refs.justificationModal.close();
      } catch (error) {
        const mensagem =
          error?.response?.data?.message || 'Não foi possível encerrar o prazo recursal';
        this.$alerta(mensagem, 'is-danger');
      } finally {
        this.loadingPrazosRecursais = false;
        this.justification.pendingPayload = null;
      }
    },
    async executarAjustePrazosComJustificativa(justificativa) {
      this.loadingPrazosRecursais = true;
      try {
        await this.salvarPrazosRecursais({
          ...this.justification.pendingPayload,
          justificativa,
        });
        this.$alerta('Prazos recursais ajustados com sucesso', 'is-success');
        this.$refs.justificationModal.close();
      } catch (error) {
        const mensagem = error?.response?.data?.message || 'Erro ao ajustar prazos recursais';
        this.$alerta(mensagem, 'is-danger');
      } finally {
        this.loadingPrazosRecursais = false;
        this.justification.pendingPayload = null;
      }
    },
    async generateMinutes(considerations) {
      this.disable.getMinutes = true;
      try {
        await this.generateAuctionMinutes({
          consideracoes: considerations,
        });
        this.$alerta('Ata obtida com sucesso', 'is-success');
        setTimeout(() => {
          if (this.$refs.tabs.items.length > 7) {
            this.selectedTab = 6;
          } else {
            this.selectedTab = 5;
          }
        }, 100);
      } catch (error) {
        this.$alerta('Erro ao obter ata', 'is-danger');
      }
      this.disable.getMinutes = false;
      this.$refs.considerationModal.close();
    },
    getWorkDay(suspensionDate, days, minimum = false) {
      let date = new Date(suspensionDate);
      date.setDate(date.getDate() + days);

      while (
        [0, 6].indexOf(date.getDay()) > -1 ||
        (this.holidays.isHoliday(date) && this.holidays.isHoliday(date).type === 'public')
      ) {
        const holiday = this.holidays.isHoliday(date);
        const day = date.getDay();
        if (day === 0 || holiday) {
          date.setDate(date.getDate() + 1);
          if (minimum) {
            date = new Date(date.setHours(0, 0, 0, 0));
          }
        } else if (day === 6) {
          date.setDate(date.getDate() + 2);
          if (minimum) {
            date = new Date(date.setHours(0, 0, 0, 0));
          }
        }
      }

      return new Date(date);
    },
    openModal(auctionId, action) {
      this.action = action;
      this.auctionId = auctionId;
      this.justification.displayDateTime = false;
      this.justification.displaySuspendText = false;
      this.justification.displayWorkingDays = false;
      this.justification.skipDoubleConfirm = false;
      this.justification.titleText = 'Justificativa';
      this.justification.placeholderText = '';
      this.justification.introText = '';

      if (action === 'insertReopeningDate') {
        this.reopeningDate.active = true;

        const lastSuspension = this.deadlines
          .slice()
          .reverse()
          .find(deadline => deadline.nome === 'Suspensão');
        const maximumDate = this.getWorkDay(lastSuspension.data_de, 60, false);

        if (process.env.VUE_APP_ENABLE_RETROACTIVE_DATE === 'true') {
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          this.reopeningDate.minimumDate = today;
        } else {
          this.reopeningDate.minimumDate = new Date(Date.now() + 10 * 60 * 1000);
        }

        this.reopeningDate.automaticSuspensionDate = new Date(lastSuspension.data_de);
        this.reopeningDate.maximumDate = new Date(maximumDate.setHours(23, 59, 59, 59));

        return;
      }
      if (action === 'suspend') {
        this.justification.displayDateTime = true;
        this.justification.displaySuspendText = true;
        this.justification.displayWorkingDays = true;
        this.justification.failText = 'Suspender';
      }
      if (action === 'suspendSineDie') {
        this.justification.displaySuspendText = true;
        this.justification.failText = 'Suspender Sine Die';
      }
      if (action === 'changeToSineDie') {
        this.justification.displaySuspendText = true;
        this.justification.failText = 'Alterar para Suspensão Sine Die';
        this.justification.titleText = 'Alterar para Suspensão Sine Die';
        this.justification.introText =
          'A data e o horário previstos para retomada da sessão serão cancelados.';
      }
      if (action === 'cancel') {
        this.justification.failText = 'Anular';
      }
      if (action === 'revoke') {
        this.justification.failText = 'Revogar';
      }

      this.justification.active = true;
      this.justification.displayFail = true;
      this.justification.displaySuccess = false;
    },
    async openConsiderationModal() {
      this.consideration = true;
    },
    reloadPage() {
      window.location.reload(true);
    },
    async startSession() {
      const unansweredClarifications = this.clarifications.filter(
        esclarecimento => esclarecimento.status === 'CRIADO',
      );
      if (unansweredClarifications.length) {
        this.$alerta('Responda todos os esclarecimentos', 'is-danger');
        return;
      }

      const unansweredImpugnations = this.impugnations.filter(
        impugnacao => impugnacao.status === 'CRIADA',
      );
      if (unansweredImpugnations.length) {
        this.$alerta('Responda todas as impugnações', 'is-danger');
        return;
      }

      this.loading.startSession = true;
      try {
        await this.openAuctionSession();
        await this.buscarItens({ ofertaCompraId: this.auction.id, v2: this.$route.query.v2 });
        this.$alerta('Sessão aberta', 'is-success');
        this.selectedTab = this.$refs.tabs.activeItem.index;
      } catch (error) {
        this.$alerta('Erro ao abrir sessão', 'is-danger');
      }
      this.loading.startSession = false;
    },
    async reopenSession(auctionId) {
      if (new Date(this.auction.data_reabertura) > new Date()) {
        this.$confirmacaoAlerta({
          message: 'A data e/ou horário atuais são menores que a previsão de retorno.',
          confirmText: 'Entendi',
          type: 'is-danger',
          hasIcon: true,
        });

        return;
      }

      try {
        const confirmation = await this.$confirmacao({
          message: `Essa ação irá reabrir o ${this.auctionType}. Deseja prosseguir?`,
          cancelText: 'Cancelar',
          confirmText: 'Continuar',
          type: 'is-info',
          hasIcon: true,
        });
        if (!confirmation) {
          return;
        }

        this.loading.general = true;
        await this.reabrirOfertaCompra(auctionId);
        this.$alerta(`${this.auctionType} reaberto`, 'is-success');
      } catch (error) {
        this.$alerta(`Erro ao reabrir ${this.auctionType}`, 'is-danger');
      }
      this.loading.general = false;
    },
    resetLoadings() {
      this.loading.alterarPregoeiro = false;
      this.loading.confirmarAlterarPregoeiro = false;
      this.loading.general = false;
      this.loadingPrazosRecursais = false;
      this.loadingPrazoManifestacaoIntencao = false;
      this.disable.getMinutes = false;
      this.loading.fetchAuction = false;
      this.loading.finishSession = false;
      this.loading.revoke = false;
      this.loading.startSession = false;
      this.loading.suspendSession = false;
      this.justification.pendingPayload = null;
    },
    async revokeElectronicAuction(data) {
      this.loading.revoke = true;
      try {
        await this.revokeAuction(data);
        this.$alerta(`${this.auctionType} revogado`, 'is-success');
      } catch (error) {
        this.$alerta(`Erro ao revogar ${this.auctionType}`, 'is-danger');
      }
      this.loading.revoke = false;
      this.$refs.justificationModal.close();
    },
    async selectTab(route) {
      const { aba } = route.query;

      if (aba) {
        if (aba === 'comprador') {
          this.selectedTab = 0;
        }
        if (aba === 'anexos') {
          this.selectedTab = 1;
        }
        if (aba === 'documentos') {
          await this.fetchCompanyDocuments(this.company.id);
          this.selectedTab = 3;
        }
        if (aba === 'esclarecimentos' || aba === 'impugnacoes') {
          if (this.$refs.tabs.items.length > 5) {
            this.selectedTab = 4;
          } else {
            this.selectedTab = 3;
          }

          if (aba === 'esclarecimentos') {
            this.internalTabSelected = 0;
          } else if (aba === 'impugnacoes') {
            this.internalTabSelected = 1;
          }
        }

        if (aba === 'recursos') {
          this.selectedTab = 6;
        }
      } else {
        this.selectedTab = 2;
      }
    },
    async processReopeningDate(dateTime) {
      this.reopeningDate.loading = true;
      try {
        await this.updateReopeningDate({ auctionId: this.auction.id, reopeningDate: dateTime });
        this.$alerta('Data de retorno definida', 'is-success');
      } catch (error) {
        this.$alerta('Erro ao definir data de retorno', 'is-danger');
      }
      this.reopeningDate.loading = false;
      this.$refs.sendTimeModal.close();
    },
    async sendJustification({ justification, dateTime }) {
      if (this.action === 'ajustePrazosRecursais') {
        await this.executarAjustePrazosComJustificativa(justification);
        return;
      }
      if (this.action === 'encerrarPrazoRecursal') {
        await this.executarEncerrarPrazoRecursal(
          this.justification.pendingPayload?.janela,
          justification,
        );
        return;
      }
      if (this.action === 'suspend') {
        await this.suspendSession({
          auctionId: this.auctionId,
          reopenDate: dateTime,
          justification,
        });
      }
      if (this.action === 'suspendSineDie') {
        await this.suspendSession({
          auctionId: this.auctionId,
          justification,
          sineDie: true,
        });
      }
      if (this.action === 'changeToSineDie') {
        await this.changeToSineDieSession({
          auctionId: this.auctionId,
          justification,
        });
      }
      if (this.action === 'revoke') {
        await this.revokeElectronicAuction({
          auctionId: this.auctionId,
          justification,
        });
      }
      if (this.action === 'cancel') {
        await this.cancelElectronicAuction({
          auctionId: this.auctionId,
          justification,
        });
      }
    },
    situationName(situation) {
      if (situation === 'Lances' && this.closedDisputeMode && this.closedMode) {
        return 'Lances - Fechado';
      }
      if (situation === 'Lances' && this.closedDisputeMode) {
        return 'Lances - Aberto';
      }
      if (situation === 'Habilitação' && this.isAdhesionQualificationContext) {
        return 'Habilitação da Adesão';
      }
      if (situation === 'Fracassada') return 'Fracassado';
      if (situation === 'Deserta') return 'Deserto';
      if (situation === 'Revogada') return 'Revogado';
      if (situation === 'Encerrada com Vencedor') return 'Encerrado com Vencedor';
      if (situation === 'Encerrada com Recurso') return 'Encerrado com Recurso';
      if (situation === 'Suspensa') return 'Suspenso';
      return situation;
    },
    async suspendSession(data) {
      this.loading.suspendSession = true;
      try {
        await this.suspendAuction(data);
        this.$alerta(`${this.auctionType} suspenso`, 'is-success');
      } catch (error) {
        this.$alerta(`Erro ao supender ${this.auctionType}`, 'is-danger');
      }
      this.loading.suspendSession = false;
      this.$refs.justificationModal.close();
    },
    async changeToSineDieSession(data) {
      if (this.loading.suspendSession) {
        return;
      }

      this.loading.suspendSession = true;
      try {
        await this.changeSuspensionToSineDie(data);
        this.$alerta('Suspensão alterada para Suspensão Sine Die', 'is-success');
      } catch (error) {
        this.$alerta('Erro ao alterar para Suspensão Sine Die', 'is-danger');
      }
      this.loading.suspendSession = false;
      this.$refs.justificationModal.close();
    },
    verifySituation(step) {
      if (Array.isArray(step)) {
        return step.indexOf(this.situation) > -1;
      }
      return this.situation === step;
    },
    async watchElectronicAuction() {
      const confirmation = await this.$confirmacao({
        message: `Você está ${
          this.auctionWatch.id ? 'desabilitando' : 'habilitando'
        } a opção para receber todas as notificações desse ${this.auctionType}. Deseja prosseguir?`,
        cancelText: 'Cancelar',
        confirmText: 'Continuar',
        type: 'is-info',
        hasIcon: true,
      });
      if (!confirmation) {
        return;
      }

      this.loading.watchAuction = true;
      try {
        await this.updateAuctionWatch({ auctionId: this.auction.id });
        this.$alerta(`Opção ${this.auctionWatch.id ? 'habilitada' : 'desabilitada'}`, 'is-success');
      } catch (error) {
        this.$alerta(
          `Erro ao ${this.auctionWatch.id ? 'habilitar' : 'desabilitar'} essa opção`,
          'is-danger',
        );
      }
      this.loading.watchAuction = false;
    },
    async tentarIniciarTourSituacao() {
      if (!this.loaded) return;
      const started = await this.rodarContextoFeatures('auction.situation', this.ctxTourSituacao, {
        prepares: this.preparesTourSituacao(),
      });
      if (!started) {
        await this.tentarIniciarTourArp();
      }
    },
    ctxTourArp(extra = {}) {
      return {
        logged: this.logged,
        canAccessArp: this.canAccessArpTab,
        journeyActive: Boolean(this.arpJourney?.active),
        ...extra,
      };
    },
    preparesTourArp() {
      return {
        'arp-jornadas': async () => {
          if (this.arpJourney?.active) {
            this.cancelarArpJourney();
            await this.$nextTick();
          }
        },
      };
    },
    async tentarIniciarTourArp() {
      if (!this.loaded || !this.logged || !this.canAccessArpTab) return;
      if (isTourActive()) return;

      await this.rodarContextoFeatures('auction.arp', this.ctxTourArp(), {
        prepares: this.preparesTourArp(),
      });
    },
    preparesTourSituacao() {
      return {
        'voltar-etapa-hints': async () => {
          await this.abrirMenuMaisAcoesParaVoltarEtapa();
        },
        'voltar-etapa-manifestacao-hints': async () => {
          await this.abrirMenuMaisAcoesParaVoltarEtapa();
        },
      };
    },
    async abrirMenuMaisAcoesParaVoltarEtapa() {
      if (!this.displayBackToProposals) return;

      const menu = this.$el && this.$el.querySelector('.auction-actions__more');
      if (!menu) return;

      const trigger = menu.querySelector('.dropdown-trigger button');
      if (trigger && !menu.classList.contains('is-active')) {
        trigger.click();
      }

      await this.$nextTick();
      await this.$nextTick();

      const itemVisivel = () => Boolean(document.querySelector('[data-tour="voltar-etapa"]'));
      for (let tentativa = 0; tentativa < 8 && !itemVisivel(); tentativa += 1) {
        // eslint-disable-next-line no-await-in-loop
        await new Promise(resolve => setTimeout(resolve, 40));
      }
    },
    reabrirTutorialSituacao(tourId) {
      if (!tourId) return;
      this.$nextTick(() => {
        this.reabrirFeature(tourId, this.ctxTourSituacao, {
          prepares: this.preparesTourSituacao(),
        });
      });
    },
  },
  async created() {
    this.$onBus('detalhe', this.displayDetails);
    this.$onBus('documentos', this.displaySendDocuments);
    this.$onBus('judgment-diligence-upload', this.displayJudgmentDiligenceUpload);
    this.$onBus('judgment-diligence-review', this.displayJudgmentDiligenceReview);
    this.$onBus('voltar', this.displayAuctionItems);

    const promises = [
      this.fetchAuction(this.$route.params.id),
      this.fetchMessages(this.$route.params.id),
      this.fetchDocumentTypes(),
    ];
    if (this.userFunction !== funcoes.VISITANTE) {
      promises.push(this.fetchUserLoggedBidder(this.$route.params.id));
    }
    await Promise.all(promises);

    this.auctionType = getOfertaCompraEnum(this.auction.tipo).titleSingular;

    await this.enterAuctionRoom();
    setTimeout(() => {
      this.fetchParticipants(this.$route.params.id);
    }, 50);

    if (this.proposalsDelivery && (this.provider || this.isBidder)) {
      await this.fetchCompanyDocuments(this.company.id);
      if (this.isBidder) {
        await this.fetchAuctionBidderDocuments({
          auctionId: this.$route.params.id,
          bidderId: this.userLoggedBidder.id,
        });
      }
    }

    if (
      this.situation === SituacaoEnum.JULGAMENTO ||
      (this.situation === SituacaoEnum.SUSPENSA &&
        this.deadlines[this.deadlines.length - 2]?.prazo_id === 19)
    ) {
      await Promise.all([
        this.fetchJudgmentDiligences({
          auctionId: this.$route.params.id,
          activeOnly: true,
        }),
        this.fetchUnresolvedJudgmentDiligences({ auctionId: this.$route.params.id }),
      ]);
    }

    this.loaded = true;
    this.$nextTick(() => {
      this.tentarIniciarTourSituacao();
    });
  },
};
</script>

<style>
.action-button {
  margin-bottom: 0.3rem;
}

.auction-container {
  margin: 0.5rem 0.2rem 1rem;
}

.situacao-com-ajuda {
  display: flex !important;
  align-items: center;
  justify-content: center;
  gap: 0.35rem;
  margin-bottom: 0;
  line-height: 1.2;
}

.situacao-com-ajuda > span {
  line-height: 1.2;
}

.situacao-tour-help {
  display: inline-flex !important;
  align-items: center;
  align-self: center;
  line-height: 1;
  margin-left: 0.35rem !important;
  /* Compensa o peso visual do título Bulma (descenders de p/ç) */
  transform: translateY(0.12em);
}

.situacao-tour-help .feature-tour-help__trigger {
  display: inline-flex !important;
  align-items: center;
  justify-content: center;
  height: auto !important;
  padding: 0 !important;
  line-height: 1 !important;
  vertical-align: middle;
}

.situacao-tour-help .feature-tour-help__trigger:hover {
  transform: scale(1.08);
}

.situacao-tour-help .icon {
  display: inline-flex !important;
  align-items: center;
  justify-content: center;
  margin: 0 !important;
}

.auction-actions {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
}

.auction-actions .level-button {
  margin-top: 0.02rem;
  margin-bottom: 0.3rem;
}

.auction-actions__more .dropdown-menu {
  min-width: 16rem;
}

.auction-actions__more .dropdown-content .dropdown-item {
  text-align: left;
}

.auction-actions__more {
  display: inline-flex;
  align-items: center;
}

.auction-actions__suspend-split {
  display: inline-flex;
  align-items: center;
}

.auction-actions__suspend-split .button {
  margin-bottom: 0;
  margin-top: 0;
}

.auction-actions__suspend-menu {
  display: inline-flex;
  align-items: center;
}

.auction-actions__suspend-menu .dropdown-trigger {
  display: inline-flex;
  align-items: center;
}

.auction-actions__suspend-main {
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
}

.auction-actions__suspend-toggle {
  border-top-left-radius: 0;
  border-bottom-left-radius: 0;
  margin-left: -1px;
  padding-left: 0.6rem;
  padding-right: 0.6rem;
}

.auction-actions__suspend-menu .dropdown-content .dropdown-item {
  text-align: left;
}

.auction-info {
  width: 300px;
}

.components {
  margin-top: 1rem !important;
}

.custom-icon {
  vertical-align: middle;
}

.disconnection-notification {
  border: unset;
}

.hero-body {
  padding: 1rem !important;
}

.icon-pending-company {
  vertical-align: middle;
}

iframe {
  width: 100%;
  height: 50vh;
}

.imminent-closure-text {
  margin-top: 1.5rem;
  margin-bottom: 2rem !important;
}

.auction-hero-level {
  display: flex !important;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  gap: 1.5rem;
}

.auction-hero-aside {
  display: flex;
  align-items: flex-start;
  flex: 1 1 auto;
  min-width: 0;
  gap: 2rem;
}

.auction-hero-meta {
  display: flex !important;
  flex-direction: row !important;
  flex-wrap: nowrap;
  align-items: flex-start !important;
  justify-content: center !important;
  flex: 1 1 auto !important;
  width: auto !important;
  max-width: none;
  margin-left: 0;
  gap: 20rem;
}

.auction-hero-status {
  display: flex;
  align-items: flex-start;
  flex: 0 0 auto;
}

.auction-hero-meta__item,
.auction-hero-meta > .level-item,
.auction-hero-meta > .level-column,
.auction-hero-status .auction-hero-meta__item {
  display: flex !important;
  flex-direction: column !important;
  align-items: center !important;
  justify-content: flex-start !important;
  flex: 0 0 auto !important;
  flex-grow: 0 !important;
  width: auto !important;
  margin: 0 !important;
}

.auction-hero-meta__item > div,
.auction-hero-meta__item > .level-item,
.auction-hero-meta__item .level-item {
  display: flex !important;
  flex-direction: column !important;
  align-items: center !important;
  justify-content: flex-start !important;
  flex: 0 0 auto !important;
  flex-grow: 0 !important;
  width: auto !important;
  margin: 0 !important;
}

.auction-hero-meta__item .heading {
  margin-bottom: 0.35rem;
  line-height: 1.25;
  min-height: 1.25em;
}

.auction-hero-meta__item .title {
  margin-bottom: 0 !important;
  padding-bottom: 0.25rem !important;
  line-height: 1.15;
  min-height: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.auction-hero-meta__local {
  margin: 0 !important;
  padding: 0;
  line-height: 1.2;
  font-size: 0.85rem;
  color: rgb(221 221 221);
}

.auction-hero-meta__item .auction-actions {
  margin-top: 0.75rem;
}

.level-button {
  margin-top: 1rem;
  margin-right: 0.2rem;
}

.level-column {
  flex-grow: 0 !important;
}

.level-item {
  margin: 0 1rem;
}

.message-pending-company {
  padding: 5px;
  margin-top: 0.4rem;
  margin-bottom: 0.4rem;
}

.notification-link {
  color: #2153b5 !important;
  font-weight: 600;
}

.notification-icon {
  vertical-align: middle;
}

.notification-text {
  margin-left: 0.5rem;
}

.minutes-pdf-download {
  margin: 2rem;
  text-align: center;
}

.minutes-pdf-download-text {
  margin-bottom: 1rem;
}

.text-pending-company {
  font-weight: 800;
  margin-left: 0.2rem;

  color: yellow;
}

.column-funcao-alterar-pregoeiro {
  min-width: 20%;
}

.email-media-alterar-pregoeiro {
  width: 50%;
}

.name-media-alterar-pregoeiro {
  white-space: normal;
  width: 50%;
}

.header-complete-alterar-pregoeiro {
  background-color: LightGray;
}

.auction-responsible-autocomplete .dropdown-menu .dropdown-content .dropdown-item {
  padding-right: 1rem;
}

.back-stage-selection__message {
  margin-bottom: 1rem;
  text-align: left;
}

.back-stage-selection__options .field-body {
  justify-content: center;
}

.back-stage-selection__cancel-button {
  margin-right: 0.5rem;
}

@media (max-width: 1023px) {
  .email-media-alterar-pregoeiro,
  .name-media-alterar-pregoeiro {
    font-size: 0.8rem;
    white-space: normal;
  }
}

@media (min-width: 300px) and (max-width: 1170px) {
  .column-direction-touch {
    flex-direction: column !important;
  }

  .auction-hero-aside {
    flex-direction: column;
    align-items: center;
    width: 100%;
  }

  .auction-hero-meta {
    justify-content: center !important;
    flex-wrap: wrap;
  }

  .margin-1 {
    margin-bottom: 1rem !important;
  }
}
</style>
